import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, Check, ChevronDown, Bell, Edit3, Eye, ShieldCheck, Plus,
  Apple, Smartphone, Tablet, Monitor, Linkedin, Twitter, Youtube,
  Lock, Sparkles, X, Loader2, TrendingUp, TrendingDown,
  ArrowLeftRight, Heart, Users, FileText, Clock, MessageSquare, Shield,
  AlertTriangle,
  Home, Briefcase, BarChart3, Settings, Flag, PoundSterling, Search,
  Filter as FilterIcon, MapPin, ChartLine, CheckSquare, FileBarChart,
} from "lucide-react";
import { BrandLogo, BrandIcon, BrandWord } from "../components/brand/BrandLogo";
import { PremiumStepper } from "../components/brand/PremiumStepper";
import { PremiumInput } from "../components/brand/PremiumInput";
import { PremiumSelect } from "../components/brand/PremiumSelect";
import { MarketingHealthCheck } from "../components/landing/MarketingHealthCheck";
import { MarketingFooter } from "../components/landing/MarketingFooter";
import { MarketingFlowCentre } from "../components/landing/MarketingFlowCentre";
import { MarketingProperties } from "../components/landing/MarketingProperties";
import { MarketingDealRoom } from "../components/landing/MarketingDealRoom";
import { MarketingIntelligence } from "../components/landing/MarketingIntelligence";
import { MarketingCommunication } from "../components/landing/MarketingCommunication";
import { MarketingAgencyFit } from "../components/landing/MarketingAgencyFit";
import { MarketingOutcomes } from "../components/landing/MarketingOutcomes";
import { MarketingReferrals } from "../components/landing/MarketingReferrals";

/* ═══════════════════════════════════════════════════════════════════
   ChainFlow — Landing page (reference-faithful rebuild)
   Light operational shell + one cinematic dark Deal Room section.
   ─────────────────────────────────────────────────────────────────── */

/* ─── Top sticky nav ─────────────────────────────────────────
   Phase 1 refinement: in-page anchors only. No stub routes.
   Sign in → existing /login. Request access → existing /signup
   (label is Request access, NOT "Book a private demo", because
   /signup is account signup, not a private-demo booking flow.
   The proper Book-a-private-demo destination lands in a later
   phase — this CTA copy will be restored at that time.)
   ─────────────────────────────────────────────────────────── */
const NAV_LINKS = [
  { label: "Product",       href: "#product",       testid: "nav-product" },
  { label: "How it works",  href: "#how-it-works",  testid: "nav-how-it-works" },
  { label: "For agencies",  href: "#for-agencies",  testid: "nav-for-agencies" },
];

const TopNav = () => {
  const nav = useNavigate();
  return (
    <nav
      className="sticky top-0 z-50 backdrop-blur-md"
      style={{
        background: "rgba(255,255,255,0.86)",
        borderBottom: "1px solid rgba(15,23,42,0.06)",
      }}
      data-testid="landing-topnav"
    >
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center gap-3 lg:gap-8">
        <Link to="/" className="shrink-0 min-w-0" data-testid="landing-brand">
          <BrandLogo tone="dark" iconSize={28} wordSize={20} />
        </Link>
        <ul className="hidden lg:flex items-center gap-8 flex-1">
          {NAV_LINKS.map((n) => (
            <li key={n.label}>
              <a
                href={n.href}
                className="text-[13px] font-semibold text-slate-700 hover:text-slate-900 transition-colors"
                data-testid={n.testid}
              >
                {n.label}
              </a>
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-2 ml-auto shrink-0">
          <button
            type="button"
            onClick={() => nav("/login")}
            className="h-9 px-3 sm:px-4 rounded-lg text-[12.5px] font-semibold text-slate-700 hover:bg-slate-100 whitespace-nowrap"
            data-testid="landing-login"
          >
            Sign in
          </button>
          <button
            type="button"
            onClick={() => nav("/signup")}
            className="h-9 px-3 sm:px-4 rounded-lg text-white text-[12.5px] font-bold inline-flex items-center gap-1.5 whitespace-nowrap"
            style={{
              background: "linear-gradient(135deg, #3B82F6 0%, #6D28D9 100%)",
              boxShadow: "0 10px 22px -10px rgba(99,102,241,0.55), inset 0 1px 0 rgba(255,255,255,0.18)",
            }}
            data-testid="landing-cta-request-access"
          >
            {/* iter-alignment · Homepage Hero V4 (2026-02) — label
                aligned with the approved hero CTA copy.  Route
                unchanged (/signup).  Shortened variant at <xl
                prevents nav crowding on 1280/1366 widths. */}
            <span className="hidden xl:inline">Book a pilot walkthrough</span>
            <span className="xl:hidden">Book a walkthrough</span>
            <ArrowRight size={12} strokeWidth={2.4} />
          </button>
        </div>
      </div>
    </nav>
  );
};

/* ─── Hero ─────────────────────────────────────────────────
   iter-alignment · Homepage Hero V4 (2026-02) — approved
   reference-faithful rebuild.  Light operational surface with
   a two-column desktop layout:

     LEFT  (~42% at ≥xl)
       · category pill
       · three-line brand headline
       · primary + CRM copy
       · 2×2 benefit grid
       · primary + secondary CTA
       · reassurance line

     RIGHT (~58% at ≥xl)
       · simplified Command Centre preview (NOT the full
         production surface — this is a deliberately dense
         marketing representation that scans "progression >
         property imagery").  4 KPI cells + START HERE eyebrow
         + 3 transaction rows with thin coloured status rails.

   Nothing in `/components/command/*` or CommandCentrePage is
   touched.  Sample data is local/presentational — no backend
   endpoint is created or called from the marketing page.
   ─────────────────────────────────────────────────────────── */

/* Local sample workspace — never leaves this component, never
   touches live state.  Status drives the thin left rail + dot
   + badge colour trio.  Order is editorial, not derived. */
const HERO_CC_KPIS = [
  { value: "8",  label: "Need attention",      tone: "rose"    },
  { value: "12", label: "Actions due today",   tone: "amber"   },
  { value: "3",  label: "Exchanges this week", tone: "violet"  },
  { value: "23", label: "Active transactions", tone: "emerald" },
];

const HERO_CC_TXNS = [
  {
    property:    "25 Landmark Pinnacle, Canary Wharf, E14",
    issue_stage: "Enquiries",
    issue_body:  "Management pack overdue",
    status:      "action_needed",
    waiting_on:  "Seller's solicitor",
    waiting_age: "3 days",
    next_action: "Chase Hollis & Green today",
    target:      "Exchange in 9 days",
  },
  {
    property:    "8 Pembroke Gardens, Kensington, W8",
    issue_stage: "Legal",
    issue_body:  "Replies to enquiries outstanding",
    status:      "watch",
    waiting_on:  "Buyer's solicitor",
    waiting_age: "2 days",
    next_action: "Confirm replies to enquiries 1\u20133",
    target:      "Exchange in 16 days",
  },
  {
    property:    "16 Vincent Square, Westminster, SW1P",
    issue_stage: "Ready to exchange",
    issue_body:  "Final authority required",
    status:      "on_track",
    waiting_on:  "Seller",
    waiting_age: "5 hours",
    next_action: "Request authority to exchange",
    target:      "Exchange in 4 days",
  },
];

const HERO_CC_STATUS = {
  action_needed: {
    rail:       "#EF4444",
    dot:        "#EF4444",
    badge_bg:   "#FEF2F2",
    badge_fg:   "#B91C1C",
    badge_text: "Action needed",
  },
  watch: {
    rail:       "#F59E0B",
    dot:        "#F59E0B",
    badge_bg:   "#FFFBEB",
    badge_fg:   "#B45309",
    badge_text: "Watch",
  },
  on_track: {
    rail:       "#10B981",
    dot:        "#10B981",
    badge_bg:   "#ECFDF5",
    badge_fg:   "#047857",
    badge_text: "On track",
  },
};

const HERO_BENEFITS = [
  "Know exactly what needs attention",
  "See who you're waiting on",
  "Know what needs to happen next",
  "Spot risk before it causes delay",
];

const HeroCommandCentrePreview = () => (
  <div
    className="rounded-2xl bg-white overflow-hidden"
    style={{
      border: "1px solid rgba(15,23,42,0.08)",
      boxShadow:
        "0 30px 60px -24px rgba(15,23,42,0.14), 0 8px 18px -10px rgba(15,23,42,0.05)",
    }}
    data-testid="landing-hero-command-centre"
  >
    {/* Header */}
    <div
      className="px-5 lg:px-6 py-4 flex items-center gap-3"
      style={{ borderBottom: "1px solid rgba(15,23,42,0.06)" }}
    >
      <span
        className="w-9 h-9 rounded-xl inline-flex items-center justify-center shrink-0"
        style={{ background: "linear-gradient(135deg,#EEF2FF,#F5F3FF)", color: "#6D28D9" }}
      >
        <BrandIcon size={18} />
      </span>
      <div className="min-w-0">
        <p className="font-display text-[15px] font-bold text-slate-900 tracking-tight leading-none">
          Flow Centre
        </p>
        <p
          className="text-[11.5px] text-slate-500 mt-1 leading-none"
          data-testid="landing-hero-cc-subheader"
        >
          Sample agency workspace · Wednesday 7 October
        </p>
      </div>
    </div>

    {/* KPI strip */}
    <div
      className="grid grid-cols-4 gap-0"
      data-testid="landing-hero-cc-kpi-strip"
      style={{ borderBottom: "1px solid rgba(15,23,42,0.06)" }}
    >
      {HERO_CC_KPIS.map((k, i) => {
        const tone = {
          rose:    "#DC2626",
          amber:   "#D97706",
          violet:  "#6D28D9",
          emerald: "#047857",
        }[k.tone];
        return (
          <div
            key={k.label}
            className="px-5 lg:px-6 py-4"
            style={i < HERO_CC_KPIS.length - 1 ? { borderRight: "1px solid rgba(15,23,42,0.05)" } : undefined}
            data-testid={`landing-hero-cc-kpi-${i}`}
          >
            <p
              className="font-display font-bold tabular-nums leading-none"
              style={{ color: tone, fontSize: "28px" }}
            >
              {k.value}
            </p>
            <p className="text-[11.5px] text-slate-500 mt-2 leading-tight">{k.label}</p>
          </div>
        );
      })}
    </div>

    {/* Section eyebrow + heading */}
    <div className="px-5 lg:px-6 pt-5 pb-3 flex items-end justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-violet-700">
          Start here
        </p>
        <p className="font-display text-[16px] font-bold text-slate-900 mt-1 leading-tight">
          Transactions needing attention
        </p>
      </div>
      <p className="text-[11px] text-slate-500 whitespace-nowrap">
        Prioritised by urgency and exchange impact
      </p>
    </div>

    {/* Transaction rows */}
    <ul className="px-5 lg:px-6 pb-5 space-y-2.5">
      {HERO_CC_TXNS.map((t, i) => {
        const s = HERO_CC_STATUS[t.status];
        return (
          <li
            key={t.property}
            className="relative rounded-xl bg-white overflow-hidden"
            style={{ border: "1px solid rgba(15,23,42,0.06)" }}
            data-testid={`landing-hero-cc-row-${i}`}
          >
            {/* Thin status rail */}
            <span
              aria-hidden="true"
              className="absolute left-0 top-0 bottom-0"
              style={{ width: "3px", background: s.rail }}
            />
            <div className="pl-4 pr-4 py-3">
              {/* Row header: dot · property · badge */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 min-w-0">
                  <span
                    aria-hidden="true"
                    className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: s.dot }}
                  />
                  <div className="min-w-0">
                    <p className="text-[13px] font-bold text-slate-900 truncate">
                      {t.property}
                    </p>
                    <p className="text-[11.5px] text-slate-500 mt-0.5">
                      <span className="text-slate-600">{t.issue_stage}</span>
                      <span className="mx-1.5 text-slate-300">·</span>
                      <span>{t.issue_body}</span>
                    </p>
                  </div>
                </div>
                <span
                  className="text-[9.5px] font-bold uppercase tracking-[0.08em] px-2 h-[20px] inline-flex items-center rounded-full shrink-0"
                  style={{ background: s.badge_bg, color: s.badge_fg }}
                  data-testid={`landing-hero-cc-badge-${i}`}
                >
                  {s.badge_text}
                </span>
              </div>

              {/* Row body: Waiting On · Next Action · Target */}
              <div
                className="grid grid-cols-3 gap-3 mt-3 pt-3"
                style={{ borderTop: "1px solid rgba(15,23,42,0.05)" }}
              >
                <div className="min-w-0">
                  <p className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-slate-400">
                    Waiting on
                  </p>
                  <p className="text-[11.5px] font-semibold text-slate-800 mt-1 truncate">
                    {t.waiting_on}
                    <span className="text-slate-400 font-normal"> · {t.waiting_age}</span>
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-slate-400">
                    Next action
                  </p>
                  <p className="text-[11.5px] font-semibold text-violet-700 mt-1 truncate">
                    {t.next_action}
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-slate-400">
                    Target
                  </p>
                  <p className="text-[11.5px] font-semibold text-slate-800 mt-1 truncate tabular-nums">
                    {t.target}
                  </p>
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  </div>
);

const Hero = () => {
  const nav = useNavigate();
  return (
    <section
      id="product"
      className="relative"
      data-testid="landing-hero"
      style={{ background: "linear-gradient(180deg, #FBFBFF 0%, #F7F8FC 100%)" }}
    >
      <div className="relative max-w-[1320px] mx-auto px-5 sm:px-6 lg:px-10 pt-10 lg:pt-14 pb-14 lg:pb-20 grid grid-cols-1 xl:grid-cols-[minmax(0,0.78fr)_minmax(0,1.02fr)] gap-10 xl:gap-14 items-start">
        {/* ─── Left copy column ─── */}
        <div className="relative pt-2">
          {/* Category pill */}
          <span
            className="inline-flex items-center gap-2 h-7 px-3 rounded-full bg-white"
            style={{ border: "1px solid rgba(15,23,42,0.08)" }}
            data-testid="landing-hero-pill"
          >
            <span
              aria-hidden="true"
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: "#6D28D9" }}
            />
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
              The operating system for property transactions
            </span>
          </span>

          {/* Headline — 3 lines, "Under control." in brand treatment */}
          <h1
            className="font-display font-bold tracking-[-0.028em] text-slate-900 mt-6 leading-[1.02]"
            style={{ fontSize: "clamp(40px, 4.6vw, 60px)" }}
            data-testid="landing-hero-title"
          >
            Every property<br />
            transaction.<br />
            <span
              style={{
                background: "linear-gradient(90deg, #4F46E5 0%, #6D28D9 55%, #7C3AED 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Under control.
            </span>
          </h1>

          {/* Explanatory paragraphs (grouped) */}
          <div className="mt-6 max-w-[520px] space-y-3">
            <p
              className="text-[15px] text-slate-700 leading-[1.55]"
              data-testid="landing-hero-primary-copy"
            >
              See every sale, blocker, owner and next action from sale
              agreed to exchange — in one live workspace built for UK
              estate agencies.
            </p>
            <p
              className="text-[13.5px] text-slate-500 leading-[1.55]"
              data-testid="landing-hero-secondary-copy"
            >
              Works alongside your CRM, giving progressors, branch
              managers and directors one place to keep every transaction
              moving.
            </p>
          </div>

          {/* Benefit grid 2×2 */}
          <ul
            className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 max-w-[560px]"
            data-testid="landing-hero-benefits"
          >
            {HERO_BENEFITS.map((b) => (
              <li
                key={b}
                className="flex items-start gap-2.5 text-[13.5px] text-slate-800"
              >
                <span
                  aria-hidden="true"
                  className="mt-0.5 w-5 h-5 rounded-full shrink-0 inline-flex items-center justify-center"
                  style={{ background: "#ECFDF5" }}
                >
                  <Check size={11} strokeWidth={3} style={{ color: "#059669" }} />
                </span>
                <span className="leading-snug">{b}</span>
              </li>
            ))}
          </ul>

          {/* CTAs */}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => nav("/signup")}
              className="h-12 px-6 rounded-xl text-white text-[13.5px] font-bold inline-flex items-center gap-2 transition-transform hover:-translate-y-[1px]"
              style={{
                background: "linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)",
                boxShadow:
                  "0 18px 36px -14px rgba(99,102,241,0.55), inset 0 1px 0 rgba(255,255,255,0.22)",
              }}
              data-testid="landing-hero-request-access"
            >
              Book a pilot walkthrough
              <ArrowRight size={14} strokeWidth={2.4} />
            </button>
            <a
              href="#how-it-works"
              className="h-12 px-5 rounded-xl inline-flex items-center gap-2 text-[13.5px] font-semibold text-slate-800 hover:text-slate-900 bg-white hover:bg-slate-50 transition-colors"
              style={{ border: "1px solid rgba(15,23,42,0.10)" }}
              data-testid="landing-hero-how-it-works"
            >
              See how it works
              <ArrowRight size={14} strokeWidth={2.4} />
            </a>
          </div>

          {/* Reassurance */}
          <p
            className="text-[12px] text-slate-500 mt-5"
            data-testid="landing-hero-reassurance"
          >
            20 minutes
            <span className="mx-2 text-slate-300">·</span>
            Realistic agency pipeline
            <span className="mx-2 text-slate-300">·</span>
            No commitment
          </p>
        </div>

        {/* ─── Right · simplified Command Centre preview ─── */}
        <div className="relative xl:pt-2">
          <HeroCommandCentrePreview />
        </div>
      </div>
    </section>
  );
};

const SoftCard = ({ children, className = "", style, ...rest }) => (
  <div
    className={`rounded-2xl bg-white ${className}`}
    style={{
      border: "1px solid rgba(15,23,42,0.06)",
      boxShadow: "0 30px 60px -28px rgba(15,23,42,0.18), 0 8px 18px -10px rgba(15,23,42,0.06)",
      ...style,
    }}
    {...rest}
  >
    {children}
  </div>
);

/* ─── Hero dashboard preview ─────────────────────────────────
   Cinematic Deal Room snapshot — matches the uploaded reference
   exactly: property header bar, left nav rail, central activity
   feed with realistic UK transaction events, right Flow Intelligence
   rail. Restrained purple accents, ultra-clean borders, calm
   whitespace. */

const HD_NAV = [
  { id: "overview",      label: "Overview",     Icon: Home,          active: false },
  { id: "deal_room",     label: "Deal Room",    Icon: Briefcase,     active: true  },
  { id: "timeline",      label: "Timeline",     Icon: Clock,         active: false },
  { id: "documents",     label: "Documents",    Icon: FileText,      active: false },
  { id: "participants",  label: "Participants", Icon: Users,         active: false },
  { id: "tasks",         label: "Tasks",        Icon: CheckSquare,   active: false, count: 6 },
  { id: "risks",         label: "Risks",        Icon: AlertTriangle, active: false, count: 3, badge: "rose" },
  { id: "insights",      label: "Insights",     Icon: BarChart3,     active: false },
  { id: "reports",       label: "Reports",      Icon: FileBarChart,  active: false },
  { id: "settings",      label: "Settings",     Icon: Settings,      active: false },
];

const HD_EVENTS = [
  { t: "10:04", title: "Mortgage offer issued",     sub: "NatWest",                       state: "completed", who: "EP", whoBg: "#FEF3C7", whoFg: "#92400E", icon: MapPin,        iconBg: "#ECFDF5", iconFg: "#10B981", day: "today" },
  { t: "09:56", title: "Searches received",         sub: "Local authority + Water",       state: "completed", who: "SL", whoBg: "#DBEAFE", whoFg: "#1D4ED8", icon: MapPin,        iconBg: "#ECFDF5", iconFg: "#10B981", day: "today" },
  { t: "09:41", title: "Seller signed TA6",         sub: "Title information document",    state: "completed", who: "MT", whoBg: "#FED7AA", whoFg: "#9A3412", icon: FileSignatureIcon, iconBg: "#FFFBEB", iconFg: "#F59E0B", day: "today" },
  { t: "09:32", title: "Management pack overdue",   sub: "Greaves Management",            state: "overdue",   who: "JS", whoBg: "#FECACA", whoFg: "#991B1B", icon: AlertTriangle, iconBg: "#FEF2F2", iconFg: "#EF4444", day: "today" },
  { t: "16:22", title: "Solicitor awaiting response", sub: "Enquiries (Q4)",              state: "waiting",   who: "RW", whoBg: "#D1FAE5", whoFg: "#065F46", icon: ShieldCheck,   iconBg: "#FFFBEB", iconFg: "#F59E0B", day: "yesterday" },
  { t: "15:48", title: "Buyer ID received",         sub: "Right to buy verified",         state: "completed", who: "JB", whoBg: "#FBCFE8", whoFg: "#9D174D", icon: ShieldCheck,   iconBg: "#ECFDF5", iconFg: "#10B981", day: "yesterday" },
  { t: "15:02", title: "Exchange target updated",   sub: "24 May 2026",                   state: "updated",   who: "EP", whoBg: "#FEF3C7", whoFg: "#92400E", icon: TrendingUp,    iconBg: "#F5F3FF", iconFg: "#7C3AED", day: "yesterday" },
];

// Inline icon helper for "Seller signed TA6" (no direct lucide match for "signed doc")
function FileSignatureIcon(props) {
  return <FileText {...props} />;
}

const HD_STATE_STYLES = {
  completed: { bg: "#ECFDF5", fg: "#047857", border: "#A7F3D0", label: "Completed" },
  overdue:   { bg: "#FEF2F2", fg: "#B91C1C", border: "#FECACA", label: "3 days overdue" },
  waiting:   { bg: "#FFFBEB", fg: "#B45309", border: "#FDE68A", label: "Waiting" },
  updated:   { bg: "#F5F3FF", fg: "#6D28D9", border: "#E9D5FF", label: "Updated" },
};

const HeroDashboardMock = () => (
  <div
    className="rounded-2xl overflow-hidden relative"
    style={{
      background: "#FFFFFF",
      border: "1px solid rgba(15,23,42,0.06)",
      boxShadow:
        "0 50px 100px -30px rgba(99,102,241,0.30), 0 24px 60px -20px rgba(15,23,42,0.15), 0 0 0 1px rgba(139,92,246,0.04)",
    }}
    data-testid="landing-hero-dashboard"
  >
    {/* ─── Property header bar ─── */}
    <div
      className="px-5 lg:px-6 py-4 flex items-center justify-between gap-4 flex-wrap"
      style={{ borderBottom: "1px solid rgba(15,23,42,0.06)" }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span
          className="w-9 h-9 rounded-xl inline-flex items-center justify-center shrink-0"
          style={{ background: "linear-gradient(135deg,#EEF2FF,#F5F3FF)", color: "#6D28D9" }}
        >
          <BrandIcon size={18} />
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-display text-[14px] lg:text-[15px] font-bold text-slate-900 tracking-tight truncate">
              23 King Street, Manchester
            </p>
            <span
              className="text-[9px] font-bold uppercase tracking-[0.12em] px-1.5 h-[18px] inline-flex items-center rounded-full"
              style={{ background: "#ECFDF5", color: "#047857", border: "1px solid #A7F3D0" }}
            >
              On track
            </span>
          </div>
          <p className="text-[10.5px] text-slate-500 mt-0.5">Ref: START-7264 · Sale · Freehold</p>
        </div>
      </div>

      <div className="flex items-center gap-4 lg:gap-5 shrink-0">
        <div className="text-right">
          <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-400">Exchange target</p>
          <p className="text-[11.5px] font-bold text-slate-900 tabular-nums mt-0.5">24 May 2026</p>
        </div>
        <div className="text-right">
          <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-400">Completion target</p>
          <p className="text-[11.5px] font-bold text-slate-900 tabular-nums mt-0.5">18 Jun 2026</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-9 h-9 shrink-0">
            <svg viewBox="0 0 36 36" className="w-9 h-9 -rotate-90">
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#EEF2F7" strokeWidth="3.4" />
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#10B981" strokeWidth="3.4" strokeDasharray="82,100" strokeLinecap="round"/>
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-slate-900 tabular-nums">82</span>
          </div>
          <div>
            <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-400">Deal health</p>
            <p className="text-[11px] font-bold text-emerald-600 mt-0.5">Good</p>
          </div>
        </div>
        <div>
          <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-400">Exchange probability</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="font-display text-[16px] font-bold text-slate-900 tabular-nums leading-none">78%</span>
            <span className="text-[10px] font-bold text-emerald-600">High</span>
          </div>
        </div>
      </div>
    </div>

    {/* ─── Body: nav · activity feed · intelligence ─── */}
    <div className="grid grid-cols-[160px_minmax(0,1fr)_220px]">

      {/* ─── Left nav rail ─── */}
      <nav className="py-4 px-2.5 shrink-0" style={{ borderRight: "1px solid rgba(15,23,42,0.06)" }}>
        <ul className="space-y-0.5">
          {HD_NAV.map(({ id, label, Icon, active, count, badge }) => (
            <li key={id}>
              <div
                className="h-9 px-2.5 rounded-lg flex items-center text-[11.5px] font-semibold gap-2.5"
                style={
                  active
                    ? {
                        background: "linear-gradient(135deg, rgba(99,102,241,0.12), rgba(139,92,246,0.08))",
                        color: "#4338CA",
                        border: "1px solid rgba(124,58,237,0.18)",
                      }
                    : { color: "#475569" }
                }
              >
                <Icon size={13} strokeWidth={2.2} style={{ color: active ? "#6D28D9" : "#94A3B8" }} />
                <span className="flex-1 truncate">{label}</span>
                {count != null && (
                  <span
                    className="text-[9px] font-bold px-1.5 h-[17px] inline-flex items-center rounded-full tabular-nums"
                    style={
                      badge === "rose"
                        ? { background: "#FEF2F2", color: "#E11D48", border: "1px solid #FECACA" }
                        : { background: "#F1F5F9", color: "#475569" }
                    }
                  >
                    {count}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </nav>

      {/* ─── Centre activity feed ─── */}
      <div className="min-w-0 px-5 py-4">
        {/* Tabs row */}
        <div className="flex items-center justify-between gap-3 mb-3" style={{ borderBottom: "1px solid rgba(15,23,42,0.06)", paddingBottom: 8 }}>
          <div className="flex items-center gap-5 text-[12px] font-bold">
            <span className="inline-flex items-center gap-1.5 text-violet-700 relative">
              Activity feed
              <span
                aria-hidden="true"
                className="absolute -bottom-[9px] left-0 right-0 h-[2px] rounded-full"
                style={{ background: "#7C3AED" }}
              />
            </span>
            <span className="text-slate-500 inline-flex items-center gap-1.5">
              Recent updates
              <span className="text-[9.5px] font-bold px-1.5 h-[16px] inline-flex items-center rounded-full bg-slate-100 text-slate-600 tabular-nums">6</span>
            </span>
          </div>
          <button
            type="button"
            className="text-[11px] font-semibold text-slate-500 inline-flex items-center gap-1.5 px-2 h-7 rounded-md hover:bg-slate-50"
          >
            <FilterIcon size={11} /> Filter
          </button>
        </div>

        {/* Today */}
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mt-2 mb-2">Today</p>
        <ul className="space-y-2.5">
          {HD_EVENTS.filter((e) => e.day === "today").map((e) => (
            <ActivityRow key={e.t + e.title} event={e} />
          ))}
        </ul>

        {/* Yesterday */}
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mt-5 mb-2">Yesterday</p>
        <ul className="space-y-2.5">
          {HD_EVENTS.filter((e) => e.day === "yesterday").map((e) => (
            <ActivityRow key={e.t + e.title} event={e} />
          ))}
        </ul>

        {/* Bottom row: Today's momentum + view full timeline link */}
        <div className="grid grid-cols-[170px_minmax(0,1fr)] gap-3 mt-5 pt-4" style={{ borderTop: "1px solid rgba(15,23,42,0.06)" }}>
          <div
            className="rounded-xl p-3"
            style={{ background: "#F8FAFC", border: "1px solid rgba(15,23,42,0.06)" }}
          >
            <p className="text-[10px] font-bold text-slate-700 mb-2">Today's momentum</p>
            <ul className="space-y-1.5">
              <li className="text-[10.5px] text-slate-600 inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#EF4444" }} />
                <b className="text-slate-900 tabular-nums">3</b> at risk
              </li>
              <li className="text-[10.5px] text-slate-600 inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#7C3AED" }} />
                <b className="text-slate-900 tabular-nums">8</b> awaiting response
              </li>
              <li className="text-[10.5px] text-slate-600 inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#10B981" }} />
                <b className="text-slate-900 tabular-nums">4</b> ready for exchange
              </li>
            </ul>
            <p className="text-[10px] font-bold text-violet-700 mt-2 inline-flex items-center gap-1">View details <ArrowRight size={9} /></p>
          </div>
          <div className="flex items-center justify-center rounded-xl"
               style={{ background: "#F5F3FF", border: "1px solid rgba(124,58,237,0.16)" }}>
            <p className="text-[11.5px] font-bold text-violet-700 inline-flex items-center gap-1.5">
              View full timeline <ArrowRight size={11} />
            </p>
          </div>
        </div>
      </div>

      {/* ─── Right Flow Intelligence rail ─── */}
      <aside className="py-4 px-3.5" style={{ borderLeft: "1px solid rgba(15,23,42,0.06)" }}>
        <div className="flex items-center gap-1.5 mb-3">
          <Sparkles size={11} className="text-violet-600" />
          <p className="text-[11px] font-bold text-slate-800">Flow Intelligence</p>
        </div>

        {/* Exchange readiness */}
        <div
          className="rounded-xl p-3 mb-2.5"
          style={{ background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.06)" }}
        >
          <p className="text-[10px] font-bold text-slate-700 mb-1.5">Exchange readiness</p>
          <div className="flex items-center gap-2">
            <div className="relative w-10 h-10 shrink-0">
              <svg viewBox="0 0 36 36" className="w-10 h-10 -rotate-90">
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#EEF2F7" strokeWidth="3.6"/>
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#7C3AED" strokeWidth="3.6" strokeDasharray="82,100" strokeLinecap="round"/>
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-slate-900 tabular-nums">82%</span>
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-bold text-emerald-600 leading-tight">On track</p>
              <p className="text-[9.5px] text-slate-500 leading-snug mt-0.5">Great progress. Keep momentum going.</p>
            </div>
          </div>
          <div className="mt-2 h-1 rounded-full overflow-hidden" style={{ background: "#EEF2F7" }}>
            <span className="block h-full rounded-full" style={{ width: "82%", background: "#7C3AED" }} />
          </div>
        </div>

        {/* AI Flow Signal */}
        <div
          className="rounded-xl p-3 mb-2.5"
          style={{ background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.06)" }}
        >
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] font-bold text-slate-700 inline-flex items-center gap-1">
              <Sparkles size={10} className="text-violet-600" /> AI Flow Signal
            </p>
            <span
              className="text-[9px] font-bold uppercase tracking-[0.12em] px-1.5 h-[16px] inline-flex items-center rounded-full"
              style={{ background: "#ECFDF5", color: "#047857", border: "1px solid #A7F3D0" }}
            >
              High
            </span>
          </div>
          <p className="text-[10px] text-slate-600 mt-1.5 leading-snug">
            Exchange probability reduced slightly due to delayed management pack and solicitor response time.
          </p>
          <p className="text-[10px] font-bold text-violet-700 mt-1.5 inline-flex items-center gap-1">View insight <ArrowRight size={9} /></p>
        </div>

        {/* Chain health */}
        <div className="rounded-xl p-3 mb-2.5" style={{ background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.06)" }}>
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] font-bold text-slate-700 inline-flex items-center gap-1">
              <Shield size={10} className="text-slate-500" /> Chain health
            </p>
            <span
              className="text-[9px] font-bold uppercase tracking-[0.12em] px-1.5 h-[16px] inline-flex items-center rounded-full"
              style={{ background: "#ECFDF5", color: "#047857", border: "1px solid #A7F3D0" }}
            >
              Stable
            </span>
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <p className="text-[10px] text-slate-600">3 of 4 links strong</p>
            <span className="flex items-center gap-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
            </span>
          </div>
        </div>

        {/* Three compact stat rows */}
        <div className="rounded-xl p-3 space-y-2" style={{ background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.06)" }}>
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-slate-600 inline-flex items-center gap-1.5"><Clock size={10} className="text-slate-500" />Mortgage expiry</p>
            <p className="text-[10.5px] font-bold tabular-nums" style={{ color: "#EA580C" }}>21 days</p>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-slate-600 inline-flex items-center gap-1.5"><MapPin size={10} className="text-slate-500" />Outstanding enquiries</p>
            <p className="text-[10.5px] font-bold text-slate-900 tabular-nums">4</p>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-slate-600 inline-flex items-center gap-1.5"><TrendingUp size={10} className="text-slate-500" />Predicted exchange window</p>
            <p className="text-[10.5px] font-bold text-slate-900 tabular-nums">15 – 25 May</p>
          </div>
        </div>

        <p className="text-[11px] font-bold text-violet-700 inline-flex items-center gap-1 mt-3">
          View all risks &amp; alerts <ArrowRight size={11} />
        </p>
      </aside>
    </div>
  </div>
);

/* Single activity row — initials avatar · icon · title/sub · state pill */
const ActivityRow = ({ event }) => {
  const s = HD_STATE_STYLES[event.state] || HD_STATE_STYLES.completed;
  const Icon = event.icon;
  return (
    <li className="grid grid-cols-[44px_28px_minmax(0,1fr)_auto_30px] items-center gap-3">
      <span className="text-[10px] font-semibold text-slate-500 tabular-nums">{event.t}</span>
      <span
        className="w-7 h-7 rounded-full inline-flex items-center justify-center shrink-0"
        style={{ background: event.iconBg, color: event.iconFg, border: `1px solid ${event.iconBg === "#FEF2F2" ? "#FECACA" : event.iconBg === "#FFFBEB" ? "#FDE68A" : event.iconBg === "#F5F3FF" ? "#E9D5FF" : "#A7F3D0"}` }}
      >
        <Icon size={12} strokeWidth={2.2} />
      </span>
      <span className="min-w-0">
        <span className="block text-[11.5px] font-bold text-slate-900 truncate leading-tight">{event.title}</span>
        <span className="block text-[10px] text-slate-500 truncate mt-0.5">{event.sub}</span>
      </span>
      <span
        className="text-[9px] font-bold uppercase tracking-[0.12em] px-1.5 h-[18px] inline-flex items-center rounded-full whitespace-nowrap"
        style={{ background: s.bg, color: s.fg, border: `1px solid ${s.border}` }}
      >
        {s.label}
      </span>
      <span
        className="w-6 h-6 rounded-full inline-flex items-center justify-center text-[9px] font-bold shrink-0"
        style={{ background: event.whoBg, color: event.whoFg }}
      >
        {event.who}
      </span>
    </li>
  );
};

/* ─── Compact four-benefit strip (Phase 1) ─────────────────
   Restrained supporting information — NOT a feature section.
   One horizontal row on desktop; 2×2 on mobile. Minimal chrome.
   ─────────────────────────────────────────────────────────── */
const KPIs = () => {
  const items = [
    { kicker: "Spot blockers early",       body: "Surface risks before they threaten exchange." },
    { kicker: "Know the next action",      body: "See what needs doing and who owns it." },
    { kicker: "Reduce progression admin",  body: "Spend less time chasing updates." },
    { kicker: "Keep everyone aligned",     body: "One source of truth for the whole team." },
  ];
  return (
    <section
      id="for-agencies"
      className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 py-8 lg:py-10"
      data-testid="landing-benefit-strip"
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-6 lg:gap-x-8">
        {items.map(({ kicker, body }) => (
          <div
            key={kicker}
            className="min-w-0"
            data-testid={`landing-benefit-${kicker.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            <p className="text-[10px] lg:text-[10.5px] font-bold uppercase tracking-[0.20em] text-violet-700">
              {kicker}
            </p>
            <p className="text-[12.5px] lg:text-[13px] text-slate-600 mt-2 leading-relaxed">
              {body}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ─── Problem transition (Phase 1) ─────────────────────────
   Short narrative bridge. ~60-70% height of a full section.
   ─────────────────────────────────────────────────────────── */
const ProblemTransition = () => (
  <section
    id="how-it-works"
    className="max-w-[1080px] mx-auto px-5 sm:px-6 lg:px-10 py-12 lg:py-16"
    data-testid="landing-problem-transition"
  >
    <div className="text-center">
      <h2 className="font-display text-[26px] sm:text-[32px] lg:text-[40px] font-bold tracking-[-0.025em] text-slate-900 leading-[1.08]">
        Transaction progress lives<br />
        <span
          style={{
            background: "linear-gradient(90deg, #4F46E5 0%, #6D28D9 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          in too many places.
        </span>
      </h2>
      <p className="text-[13.5px] sm:text-[15px] text-slate-500 leading-relaxed mt-5">
        Emails. CRM notes. Calls. Spreadsheets.<br />
        Solicitor updates. Broker updates.
      </p>
      <p className="text-[15px] sm:text-[17px] font-semibold text-slate-900 mt-5">
        ChainFlow brings it together.
      </p>
    </div>
  </section>
);

/* ─── Deal Room dark showcase ─────────────────────────────
   Cinematic Deal Room snapshot matching the operational reference:
   ambient lavender glow halo, midnight-navy gradient surface, header
   bar with property + status pill + participant stack + exchange &
   completion targets + Deal Health donut + Exchange Probability,
   centre activity feed grouped by Today/Yesterday with timestamps,
   completion pills and initials avatars, right rail with Chain
   Intelligence + Key Dates + At Risk + Linked Transactions. */

const DR_NAV_ICONS = [Home, FileText, Edit3, FileText, Users, CheckSquare, Shield, BarChart3, Settings];

const DR_TODAY = [
  { t: "10:04", title: "Instruction received",    sub: "by Emma Parker",            status: "Completed", tone: "emerald", who: "EP", whoBg: "#FDE68A" },
  { t: "09:56", title: "GA agreed",                sub: "by James Smith",            status: "Completed", tone: "emerald", who: "JS", whoBg: "#C4B5FD" },
  { t: "09:41", title: "TA6 received",             sub: "Signed by Mark Thompson",   status: "Completed", tone: "emerald", who: "MT", whoBg: "#FDBA74" },
  { t: "09:32", title: "Searches ordered",         sub: "Local Authority + Drainage", status: "Completed", tone: "emerald", who: "SL", whoBg: "#FBCFE8" },
];

const DR_YESTERDAY = [
  { t: "16:22", title: "Mortgage offer issued – Nationwide", sub: "Offer amount £298,000", status: "Completed", tone: "emerald", who: "RW", whoBg: "#86EFAC", iconBg: "amber" },
  { t: "15:48", title: "Draft contracts received", sub: "by Sarah Lee",              status: "Completed", tone: "emerald", who: "SL", whoBg: "#C4B5FD", iconBg: "blue" },
  { t: "15:02", title: "Enquiries raised",         sub: "3 enquiries",               status: "In progress", tone: "amber",   who: "JS", whoBg: "#FCA5A5", iconBg: "rose" },
];

const ToneTokens = {
  emerald: { dotBg: "#10B981", chipBg: "rgba(16,185,129,0.14)",  chipFg: "#6EE7B7", chipBorder: "rgba(16,185,129,0.30)", iconBg: "rgba(16,185,129,0.14)", iconFg: "#6EE7B7", iconBorder: "rgba(16,185,129,0.30)" },
  amber:   { dotBg: "#F59E0B", chipBg: "rgba(245,158,11,0.14)",  chipFg: "#FCD34D", chipBorder: "rgba(245,158,11,0.30)", iconBg: "rgba(248,113,113,0.16)", iconFg: "#FCA5A5", iconBorder: "rgba(248,113,113,0.30)" },
  blue:    { iconBg: "rgba(59,130,246,0.16)", iconFg: "#93C5FD", iconBorder: "rgba(59,130,246,0.30)" },
  rose:    { iconBg: "rgba(244,63,94,0.16)",  iconFg: "#FCA5A5", iconBorder: "rgba(244,63,94,0.30)" },
};

const DRTimelineRow = ({ ev, last }) => {
  const t = ToneTokens[ev.tone] || ToneTokens.emerald;
  const iconTone = ToneTokens[ev.iconBg] || (ev.iconBg ? ToneTokens[ev.iconBg] : t);
  return (
    <li className="relative grid grid-cols-[48px_22px_minmax(0,1fr)_auto_28px] items-center gap-3 py-2.5">
      <span className="text-[11px] font-semibold text-slate-400 tabular-nums">{ev.t}</span>
      {/* Spine dot + vertical line */}
      <span className="relative inline-flex items-center justify-center w-5 h-5">
        <span className="w-2 h-2 rounded-full" style={{ background: t.dotBg, boxShadow: `0 0 10px ${t.dotBg}66` }} />
        {!last && <span aria-hidden="true" className="absolute top-3 left-1/2 -translate-x-1/2 w-px h-10" style={{ background: "rgba(167,139,250,0.18)" }} />}
      </span>
      <span className="min-w-0">
        <span className="block text-[12.5px] font-bold text-white leading-tight inline-flex items-center gap-2">
          <span
            className="w-6 h-6 rounded-md inline-flex items-center justify-center shrink-0"
            style={{ background: iconTone.iconBg, border: `1px solid ${iconTone.iconBorder}`, color: iconTone.iconFg }}
          >
            <FileText size={11} />
          </span>
          {ev.title}
        </span>
        {ev.sub && <span className="block text-[10.5px] text-slate-400 mt-0.5 ml-8">{ev.sub}</span>}
      </span>
      <span
        className="text-[10px] font-bold px-2 h-[20px] inline-flex items-center justify-center rounded-full whitespace-nowrap"
        style={{ background: t.chipBg, color: t.chipFg, border: `1px solid ${t.chipBorder}` }}
      >
        {ev.status}
      </span>
      <span
        className="w-7 h-7 rounded-full inline-flex items-center justify-center text-[10px] font-bold text-slate-900"
        style={{ background: ev.whoBg }}
      >
        {ev.who}
      </span>
    </li>
  );
};

const DealRoomShowcase = () => (
  <section className="max-w-[1320px] mx-auto px-6 lg:px-10 pb-16" data-testid="landing-deal-room">
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.65fr)_minmax(0,1.65fr)] gap-10 lg:gap-12 items-start">
      {/* Left copy */}
      <div className="pt-3">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-violet-700">The Deal Room</p>
        <h2 className="font-display text-[40px] lg:text-[52px] font-bold tracking-[-0.025em] leading-[1.02] mt-4" style={{ color: "#0F172A" }}>
          Everything.<br/>Everyone.<br/>
          <span style={{ background: "linear-gradient(90deg, #3B82F6 0%, #6366F1 60%, #7C3AED 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            All in one place.
          </span>
        </h2>
        <p className="text-[14px] text-slate-600 leading-relaxed mt-5 max-w-[420px]">
          The central workspace for every transaction. Built to keep all parties aligned and deals moving forward.
        </p>
        <ul className="mt-7 space-y-3.5">
          {[
            "Live progression timeline",
            "Tasks, deadlines and next steps",
            "Documents always in the right place",
            "Messages and updates in context",
            "Risk alerts and chain visibility",
          ].map((t) => (
            <li key={t} className="inline-flex items-center gap-3 text-[13.5px] font-semibold text-slate-800">
              <span className="w-5 h-5 rounded-full inline-flex items-center justify-center shrink-0"
                    style={{ background: "#EDE9FE", border: "1px solid #DDD6FE" }}>
                <Check size={11} strokeWidth={3} className="text-violet-700" />
              </span>
              {t}
            </li>
          ))}
        </ul>
        <Link to="/login" className="inline-flex items-center gap-1.5 mt-7 text-[13.5px] font-bold text-violet-700">
          Explore the Deal Room <ArrowRight size={13} />
        </Link>
      </div>

      {/* Right immersive showcase */}
      <div className="relative">
        {/* Ambient lavender glow halo */}
        <span aria-hidden="true" className="absolute -inset-10 pointer-events-none"
              style={{ background: "radial-gradient(closest-side, rgba(167,139,250,0.30), rgba(124,58,237,0.10) 55%, transparent 80%)", filter: "blur(28px)" }} />
        <span aria-hidden="true" className="absolute -top-8 -right-12 w-[320px] h-[320px] rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(139,92,246,0.28), transparent 70%)", filter: "blur(40px)" }} />

        <div
          className="relative rounded-2xl overflow-hidden hidden md:block"
          style={{
            background: "linear-gradient(160deg, #0F1132 0%, #0B0D26 55%, #07081B 100%)",
            border: "1px solid rgba(139,92,246,0.18)",
            boxShadow:
              "0 50px 100px -28px rgba(99,102,241,0.35), 0 24px 60px -24px rgba(15,23,42,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
          }}
        >
          <DealRoomDarkMock />
        </div>
      </div>
    </div>
  </section>
);

const DealRoomDarkMock = () => (
  <div className="grid grid-cols-[56px_minmax(0,1.55fr)_minmax(0,0.7fr)] text-white">
    {/* Left nav rail */}
    <div className="py-5 px-2 flex flex-col items-center gap-2.5"
         style={{ borderRight: "1px solid rgba(255,255,255,0.04)" }}>
      <span
        className="w-10 h-10 rounded-xl inline-flex items-center justify-center mb-1.5"
        style={{
          background: "linear-gradient(135deg, rgba(124,58,237,0.30), rgba(99,102,241,0.18))",
          border: "1px solid rgba(167,139,250,0.30)",
          boxShadow: "0 0 18px rgba(124,58,237,0.40), inset 0 1px 0 rgba(255,255,255,0.10)",
        }}
      >
        <BrandIcon size={16} />
      </span>
      {DR_NAV_ICONS.map((Icon, i) => (
        <span
          key={i}
          className="w-9 h-9 rounded-lg inline-flex items-center justify-center"
          style={
            i === 0
              ? {
                  background: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)",
                  color: "#FFFFFF",
                  boxShadow: "0 8px 20px -8px rgba(124,58,237,0.65), 0 0 12px rgba(167,139,250,0.40), inset 0 1px 0 rgba(255,255,255,0.16)",
                }
              : { color: "rgba(167,139,250,0.55)" }
          }
        >
          <Icon size={14} strokeWidth={1.8} />
        </span>
      ))}
    </div>

    {/* Main canvas */}
    <div className="min-w-0">
      {/* Property header */}
      <div className="px-6 py-5 flex items-center justify-between gap-4 flex-wrap"
           style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <p className="font-display text-[19px] font-bold tracking-tight text-white">23 King Street, Manchester</p>
            <span className="text-[9.5px] font-bold uppercase tracking-[0.12em] px-2 h-[22px] inline-flex items-center rounded-full"
                  style={{ background: "rgba(16,185,129,0.15)", color: "#6EE7B7", border: "1px solid rgba(16,185,129,0.30)" }}>
              On Track
            </span>
          </div>
          <p className="text-[11px] mt-1" style={{ color: "rgba(167,139,250,0.55)" }}>
            Ref: START-7264 · Sale · Freehold
          </p>
        </div>
        <div className="flex items-center gap-5 flex-wrap">
          {/* Participant stack */}
          <span className="flex -space-x-1.5">
            {[["EP", "#FDE68A"], ["SL", "#C4B5FD"], ["MT", "#FDBA74"], ["RW", "#86EFAC"]].map(([i, c]) => (
              <span key={i} className="w-7 h-7 rounded-full inline-flex items-center justify-center text-[9.5px] font-bold text-slate-900"
                    style={{ background: c, border: "2px solid #0B0E22" }}>
                {i}
              </span>
            ))}
            <span className="w-7 h-7 rounded-full inline-flex items-center justify-center text-[9.5px] font-bold text-violet-200"
                  style={{ background: "rgba(124,58,237,0.30)", border: "2px solid #0B0E22" }}>
              +2
            </span>
          </span>

          {/* Targets */}
          <div>
            <p className="text-[9px] font-semibold uppercase tracking-[0.12em]" style={{ color: "rgba(148,163,184,0.7)" }}>Exchange target</p>
            <p className="text-[12px] font-bold tabular-nums mt-0.5 text-white">24 May 2026</p>
          </div>
          <div>
            <p className="text-[9px] font-semibold uppercase tracking-[0.12em]" style={{ color: "rgba(148,163,184,0.7)" }}>Completion target</p>
            <p className="text-[12px] font-bold tabular-nums mt-0.5 text-white">18 Jun 2026</p>
          </div>

          {/* Deal health donut */}
          <div className="flex items-center gap-2">
            <div className="relative w-10 h-10">
              <svg viewBox="0 0 36 36" className="w-10 h-10 -rotate-90">
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3.4"/>
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#10B981" strokeWidth="3.4" strokeDasharray="82,100" strokeLinecap="round"/>
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-white tabular-nums">82</span>
            </div>
            <div>
              <p className="text-[9px] font-semibold uppercase tracking-[0.12em]" style={{ color: "rgba(148,163,184,0.7)" }}>Deal health</p>
              <p className="text-[11px] font-bold text-emerald-300 mt-0.5">Good</p>
            </div>
          </div>

          {/* Exchange probability */}
          <div>
            <p className="text-[9px] font-semibold uppercase tracking-[0.12em]" style={{ color: "rgba(148,163,184,0.7)" }}>Exchange probability</p>
            <p className="font-display text-[18px] font-bold text-emerald-300 tabular-nums leading-none mt-1 inline-flex items-center gap-1.5">
              78% <span className="text-[10px] font-bold text-emerald-200/80">High</span>
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 pt-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-5 text-[12px] font-bold">
          <span className="text-white relative pb-2">
            Activity feed
            <span aria-hidden="true" className="absolute -bottom-[1px] left-0 right-0 h-[2px] rounded-full" style={{ background: "#A78BFA" }} />
          </span>
          <span className="inline-flex items-center gap-1.5" style={{ color: "rgba(167,139,250,0.55)" }}>
            Recent updates
            <span className="text-[9.5px] font-bold px-1.5 h-[16px] inline-flex items-center rounded-full"
                  style={{ background: "rgba(255,255,255,0.06)", color: "rgba(203,213,225,0.85)" }}>6</span>
          </span>
        </div>
        <button type="button" className="text-[11px] font-semibold inline-flex items-center gap-1.5 h-7 px-2 rounded-md"
                style={{ color: "rgba(167,139,250,0.65)" }}>
          <FilterIcon size={11} /> Filter
        </button>
      </div>

      {/* Today */}
      <div className="px-6 pt-4">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: "rgba(148,163,184,0.65)" }}>Today</p>
        <ul className="mt-1">
          {DR_TODAY.map((ev, i) => <DRTimelineRow key={ev.t + ev.title} ev={ev} last={i === DR_TODAY.length - 1} />)}
        </ul>
      </div>

      {/* Yesterday */}
      <div className="px-6 pt-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: "rgba(148,163,184,0.65)" }}>Yesterday</p>
        <ul className="mt-1">
          {DR_YESTERDAY.map((ev, i) => <DRTimelineRow key={ev.t + ev.title} ev={ev} last={i === DR_YESTERDAY.length - 1} />)}
        </ul>
      </div>

      {/* Target rows */}
      <div className="px-6 pt-3 pb-6 space-y-2">
        {[
          ["24 May", "Exchange target",   "In 18 days", "violet"],
          ["18 Jun", "Completion target", "In 43 days", "slate"],
        ].map(([d, label, cd, tone]) => (
          <div key={label} className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl"
               style={{
                 background: tone === "violet" ? "rgba(124,58,237,0.10)" : "rgba(255,255,255,0.03)",
                 border: tone === "violet" ? "1px solid rgba(167,139,250,0.22)" : "1px solid rgba(255,255,255,0.05)",
               }}>
            <span className="w-2 h-2 rounded-full" style={{ background: tone === "violet" ? "#A78BFA" : "#64748B" }} />
            <span className="text-[11px] font-bold text-violet-200 tabular-nums w-12">{d}</span>
            <span className="text-[12px] font-bold text-white flex-1">{label}</span>
            <span className="text-[10px] font-bold px-2 h-[22px] inline-flex items-center rounded-full"
                  style={{ background: "rgba(255,255,255,0.05)", color: "rgba(226,232,240,0.85)", border: "1px solid rgba(255,255,255,0.08)" }}>
              {cd}
            </span>
          </div>
        ))}
      </div>
    </div>

    {/* Right intelligence rail */}
    <aside className="py-5 px-4 space-y-4"
           style={{ borderLeft: "1px solid rgba(255,255,255,0.06)" }}>
      {/* Chain Intelligence */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-3">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: "rgba(167,139,250,0.65)" }}>Chain Intelligence</p>
          <span className="text-[9px] font-bold uppercase tracking-[0.12em] px-1.5 h-[18px] inline-flex items-center rounded-full"
                style={{ background: "rgba(245,158,11,0.18)", color: "#FCD34D", border: "1px solid rgba(245,158,11,0.32)" }}>
            ELEVATED
          </span>
        </div>
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[12px] font-bold text-white">Chain risk</p>
            <p className="text-[10.5px] mt-1.5" style={{ color: "rgba(203,213,225,0.85)" }}><b className="text-white">3</b> links in the chain</p>
            <p className="text-[10.5px]" style={{ color: "rgba(203,213,225,0.85)" }}><b className="text-white">2</b> dependent transactions</p>
          </div>
          <div className="relative w-14 h-14 shrink-0">
            <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90">
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3.4"/>
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#F59E0B" strokeWidth="3.4" strokeDasharray="55,100" strokeLinecap="round"/>
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[9.5px] font-bold text-amber-300 tabular-nums">Medium</span>
          </div>
        </div>
        <p className="text-[10.5px] font-bold mt-2 inline-flex items-center gap-1" style={{ color: "#C4B5FD" }}>View chain <ArrowRight size={9} /></p>
      </div>

      {/* Key Dates */}
      <div className="pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] mb-2.5" style={{ color: "rgba(167,139,250,0.65)" }}>Key dates</p>
        {[
          ["Mortgage expires", "20 May 2026", "In 9 days"],
          ["Exchange target",  "24 May 2026", "In 18 days"],
          ["Completion target", "18 Jun 2026", "In 43 days"],
        ].map(([k, v, cd]) => (
          <div key={k} className="mb-2.5">
            <div className="flex items-center justify-between">
              <p className="text-[11.5px] font-bold text-white">{k}</p>
              <span className="text-[10px]" style={{ color: "rgba(148,163,184,0.85)" }}>{cd}</span>
            </div>
            <p className="text-[10px] tabular-nums" style={{ color: "rgba(167,139,250,0.55)" }}>{v}</p>
          </div>
        ))}
        <p className="text-[10.5px] font-bold mt-1 inline-flex items-center gap-1" style={{ color: "#C4B5FD" }}>View full timeline <ArrowRight size={9} /></p>
      </div>

      {/* At Risk */}
      <div className="pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        <div className="flex items-center justify-between gap-2 mb-2.5">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: "rgba(167,139,250,0.65)" }}>At risk</p>
          <span className="text-[9px] font-bold uppercase tracking-[0.10em] px-1.5 h-[17px] inline-flex items-center rounded-full"
                style={{ background: "rgba(244,63,94,0.18)", color: "#FCA5A5", border: "1px solid rgba(244,63,94,0.32)" }}>
            2 RISKS
          </span>
        </div>
        {[
          ["Management pack overdue", "3 days overdue"],
          ["Awaiting enquiries response", "Solicitor response time high"],
        ].map(([k, sub]) => (
          <div key={k} className="mb-2 flex items-start gap-2">
            <span className="w-2.5 h-2.5 rounded-full mt-1 shrink-0" style={{ background: "#F43F5E", boxShadow: "0 0 8px rgba(244,63,94,0.55)" }} />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-bold text-white leading-tight">{k}</p>
              <p className="text-[10px] mt-0.5" style={{ color: "#FCA5A5" }}>{sub}</p>
            </div>
          </div>
        ))}
        <p className="text-[10.5px] font-bold mt-1 inline-flex items-center gap-1" style={{ color: "#C4B5FD" }}>View all risks &amp; alerts <ArrowRight size={9} /></p>
      </div>

      {/* Linked Transactions */}
      <div className="pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        <div className="flex items-center justify-between gap-2 mb-2.5">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: "rgba(167,139,250,0.65)" }}>Linked transactions</p>
          <span className="text-[10px]" style={{ color: "rgba(148,163,184,0.85)" }}>2 links</span>
        </div>
        {[
          ["12 Oak Avenue, Manchester", "Buyer in chain",  "On track", "#10B981"],
          ["8 Park Road, Manchester",   "Seller in chain", "At risk",  "#F59E0B"],
        ].map(([addr, role, st, color]) => (
          <div key={addr} className="mb-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] font-bold text-white truncate">{addr}</p>
              <span className="text-[9.5px] font-bold inline-flex items-center gap-1 shrink-0" style={{ color }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
                {st}
              </span>
            </div>
            <p className="text-[10px] mt-0.5" style={{ color: "rgba(167,139,250,0.55)" }}>{role}</p>
          </div>
        ))}
        <p className="text-[10.5px] font-bold mt-1 inline-flex items-center gap-1" style={{ color: "#C4B5FD" }}>View chain <ArrowRight size={9} /></p>
      </div>
    </aside>
  </div>
);

/* ─── Mobile continuity ──────────────────────────────── */
/* ─── Mobile Continuity · cinematic operational composition ─────────
   Premium replacement for the previous device row. Inspired by Apple
   product marketing × Stripe × Linear × Notion. Cinematic, immersive,
   editorial, enterprise-grade — built for the way agents actually work.
   ─────────────────────────────────────────────────────────────────── */
const MOB_BENEFITS = [
  { Icon: Bell,        title: "Never miss what's blocking exchange",  sub: "Instant alerts surface risks and delays before they impact your chain." },
  { Icon: Edit3,       title: "Respond before delays become risks",   sub: "Reply, upload and approve actions in seconds, from anywhere." },
  { Icon: ShieldCheck, title: "Keep transactions progressing",        sub: "Stay aligned with all parties and keep momentum between viewings." },
  { Icon: Eye,         title: "Full operational visibility",          sub: "Everything you need, in your pocket. Even offline." },
];

const MOB_FLOAT_CARDS = [
  // Left side — anchored to iPhone
  { id: "mortgage-issued", Icon: PoundSterling, kicker: "Mortgage offer issued", title: "NatWest · 2 mins ago", tone: "violet",  pillTone: null,    pos: "top-[6%] left-[2%] md:left-[-24px]" },
  { id: "solicitor",       Icon: ShieldCheck,   kicker: "Solicitor awaiting reply", title: "4 days inactive",   tone: "amber",   pillTone: "rose",  pillText: "High risk", pos: "top-[34%] left-[-2%] md:left-[-44px]" },
  { id: "searches",        Icon: Search,        kicker: "Searches returned", title: "Local Authority · Just now", tone: "emerald", pillTone: null, pos: "bottom-[18%] left-[3%] md:left-[-30px]" },
  // Right side — anchored to iPad
  { id: "exchange-ahead",  Icon: TrendingUp,    kicker: "Exchange in 9 days", title: "Stay on track to hit your target", tone: "violet", pillTone: null, pos: "top-[2%] right-[2%] md:right-[-30px]" },
  { id: "risks",           Icon: AlertTriangle, kicker: "2 risks detected", title: "Tap to review", tone: "violet", pillTone: null, withArrow: true, pos: "bottom-[10%] right-[2%] md:right-[-40px]" },
];

const MOB_KPIS = [
  { label: "Deal health",        value: 82, suffix: "/100", spark: [40, 48, 52, 60, 66, 74, 82], tone: "violet"  },
  { label: "Exchange probability", value: 78, suffix: "%",   spark: [55, 58, 62, 66, 70, 74, 78], tone: "emerald" },
  { label: "Active risks",       value: 2,  suffix: "",     spark: [4, 3, 3, 2, 2, 2, 2],         tone: "amber",   inverse: true },
  { label: "Chain visibility",   value: 100, suffix: "%",   spark: [70, 75, 82, 88, 92, 96, 100], tone: "slate" },
];

const MobMiniSpark = ({ points = [], stroke = "#7C3AED" }) => {
  const w = 88, h = 24, pad = 2;
  const max = Math.max(...points), min = Math.min(...points);
  const range = Math.max(1, max - min);
  const step = (w - pad * 2) / Math.max(1, points.length - 1);
  const d = points.map((p, i) => {
    const x = pad + i * step;
    const y = h - pad - ((p - min) / range) * (h - pad * 2);
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const last = points[points.length - 1];
  const lastX = pad + (points.length - 1) * step;
  const lastY = h - pad - ((last - min) / range) * (h - pad * 2);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-[88px] h-6 shrink-0" aria-hidden="true">
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastX} cy={lastY} r="2.2" fill={stroke} />
    </svg>
  );
};

const MobFloatingCard = ({ Icon, kicker, title, tone, pos, pillTone, pillText, withArrow }) => {
  const tones = {
    emerald: { bg: "#ECFDF5", fg: "#047857", ring: "rgba(16,185,129,0.22)" },
    amber:   { bg: "#FFFBEB", fg: "#B45309", ring: "rgba(245,158,11,0.22)" },
    violet:  { bg: "#F5F3FF", fg: "#6D28D9", ring: "rgba(124,58,237,0.22)" },
  }[tone];
  const pillTones = {
    rose:   { bg: "#FEF2F2", fg: "#B91C1C", border: "#FECACA" },
    violet: { bg: "#F5F3FF", fg: "#6D28D9", border: "#E9D5FF" },
  };
  const pill = pillTone ? pillTones[pillTone] : null;
  return (
    <div
      className={`absolute ${pos} hidden md:flex items-center gap-3 px-3.5 py-2.5 rounded-2xl bg-white/95 backdrop-blur-xl shadow-[0_24px_50px_-20px_rgba(15,23,42,0.22),0_8px_18px_-10px_rgba(124,58,237,0.18)] cf-fade-up z-20 max-w-[230px]`}
      style={{ border: "1px solid rgba(15,23,42,0.06)" }}
    >
      <span
        className="w-9 h-9 rounded-xl inline-flex items-center justify-center shrink-0"
        style={{ background: tones.bg, color: tones.fg, border: `1px solid ${tones.ring}` }}
      >
        <Icon size={15} strokeWidth={2.1} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[11.5px] font-bold leading-tight" style={{ color: "#0F172A" }}>{kicker}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <p className="text-[10.5px] truncate leading-tight" style={{ color: "#64748B" }}>{title}</p>
          {pill && (
            <span className="text-[8.5px] font-bold uppercase tracking-[0.10em] px-1.5 h-[15px] inline-flex items-center rounded-full shrink-0"
                  style={{ background: pill.bg, color: pill.fg, border: `1px solid ${pill.border}` }}>
              {pillText}
            </span>
          )}
        </div>
      </div>
      {withArrow && (
        <ArrowRight size={13} className="shrink-0" style={{ color: "#94A3B8" }} />
      )}
    </div>
  );
};

/* iPad render — full operational Deal Room with right intelligence rail */
const MobIPad = () => (
  <div
    className="relative w-[540px] max-w-full h-[400px] rounded-[24px] p-[8px]"
    style={{
      background: "linear-gradient(180deg, #1B1D3A 0%, #0E0F22 100%)",
      boxShadow: "0 60px 120px -30px rgba(60,40,180,0.40), 0 30px 60px -20px rgba(15,23,42,0.45), inset 0 0 0 1px rgba(255,255,255,0.06)",
    }}
  >
    <div
      className="w-full h-full rounded-[18px] overflow-hidden relative"
      style={{ background: "linear-gradient(160deg, #FFFFFF 0%, #FAFAFD 100%)" }}
    >
      {/* Top header bar */}
      <div className="flex items-center gap-3 px-3 py-2.5 border-b" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
        <p className="font-display text-[11.5px] font-bold leading-none truncate" style={{ color: "#0F172A" }}>23 King Street, Manchester</p>
        <span className="text-[7.5px] font-bold uppercase tracking-[0.14em] px-1.5 h-[14px] inline-flex items-center rounded-full"
              style={{ background: "#ECFDF5", color: "#047857", border: "1px solid #A7F3D0" }}>On Track</span>
        {/* Participant stack */}
        <div className="flex items-center ml-1">
          {[
            { i: "EP", g: "linear-gradient(135deg,#6D28D9,#4F46E5)" },
            { i: "SL", g: "linear-gradient(135deg,#0EA5E9,#0369A1)" },
            { i: "MT", g: "linear-gradient(135deg,#10B981,#047857)" },
          ].map((a, i) => (
            <span key={i} className="w-5 h-5 rounded-full text-white text-[7.5px] font-bold inline-flex items-center justify-center ring-2 ring-white" style={{ background: a.g, marginLeft: i === 0 ? 0 : -6 }}>{a.i}</span>
          ))}
          <span className="w-5 h-5 rounded-full text-[7.5px] font-bold inline-flex items-center justify-center ring-2 ring-white -ml-1.5" style={{ background: "#F1F5F9", color: "#64748B" }}>+3</span>
        </div>
        <span className="ml-auto text-[9px] inline-flex items-baseline gap-1.5" style={{ color: "#64748B" }}>
          <span className="font-bold uppercase tracking-[0.10em]" style={{ color: "#94A3B8" }}>Exchange</span>
          <span className="font-bold tabular-nums" style={{ color: "#0F172A" }}>24 May 2026</span>
        </span>
        <span className="text-[9px] inline-flex items-baseline gap-1.5" style={{ color: "#64748B" }}>
          <span className="font-bold uppercase tracking-[0.10em]" style={{ color: "#94A3B8" }}>Completion</span>
          <span className="font-bold tabular-nums" style={{ color: "#0F172A" }}>18 Jun 2026</span>
        </span>
      </div>

      {/* Body grid */}
      <div className="grid grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] gap-2.5 p-2.5 h-[calc(100%-43px)]">
        {/* LEFT — Timeline */}
        <div className="rounded-lg p-2.5 overflow-hidden" style={{ background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.05)" }}>
          <p className="font-display text-[11px] font-bold mb-1.5" style={{ color: "#0F172A" }}>Timeline</p>
          <ul className="space-y-1">
            {[
              { t: "Instruction received",    sub: "by Emma Parker",                d: "12 Apr 2026", dot: "#10B981" },
              { t: "GA agreed",               sub: "by James Smith",                d: "14 Apr 2026", dot: "#10B981" },
              { t: "TA6 received",            sub: "Signed by Mark Thompson",       d: "16 Apr 2026", dot: "#10B981" },
              { t: "Searches ordered",        sub: "Local Authority + Drainage",    d: "17 Apr 2026", dot: "#10B981" },
              { t: "Mortgage offer received", sub: "NatWest · £296,000",            d: "22 Apr 2026", dot: "#F59E0B" },
              { t: "Draft contracts received",sub: "by Sarah Lee",                  d: "08 May 2026", dot: "#F59E0B" },
              { t: "Enquiries raised",        sub: "3 enquiries",                   d: "In progress", dot: "#F59E0B" },
              { t: "Exchange target",         sub: null,                            d: "24 May 2026", dot: "#fff", active: true },
              { t: "Completion target",       sub: null,                            d: "18 Jun 2026", dot: "#10B981" },
            ].map((row, i) => (
              <li
                key={i}
                className="grid grid-cols-[8px_minmax(0,1fr)_auto] items-center gap-2 px-1.5 py-1 rounded-md"
                style={row.active
                  ? { background: "linear-gradient(135deg, #6D28D9 0%, #4F46E5 100%)" }
                  : { background: "transparent" }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: row.dot, boxShadow: row.active ? "0 0 0 3px rgba(255,255,255,0.30)" : "none" }} />
                <span className="min-w-0">
                  <span className="block text-[9.5px] font-bold leading-tight truncate"
                        style={{ color: row.active ? "#FFFFFF" : "#0F172A" }}>{row.t}</span>
                  {row.sub && (
                    <span className="block text-[8px] truncate" style={{ color: row.active ? "rgba(255,255,255,0.75)" : "#94A3B8" }}>{row.sub}</span>
                  )}
                </span>
                <span className="text-[8px] tabular-nums" style={{ color: row.active ? "rgba(255,255,255,0.75)" : "#94A3B8" }}>{row.d}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* RIGHT — Intelligence rail */}
        <div className="flex flex-col gap-2.5 overflow-hidden">
          {/* Chain Intelligence */}
          <div className="rounded-lg p-2.5" style={{ background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.05)" }}>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[7.5px] font-bold uppercase tracking-[0.12em]" style={{ color: "#94A3B8" }}>Chain Intelligence</p>
              <span className="text-[7.5px] font-bold uppercase tracking-[0.10em] px-1.5 h-[14px] inline-flex items-center rounded-full"
                    style={{ background: "#FFFBEB", color: "#B45309", border: "1px solid #FDE68A" }}>Medium</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-[9.5px] font-bold" style={{ color: "#0F172A" }}>Chain risk</p>
                <p className="text-[8.5px]" style={{ color: "#64748B" }}>3 links in the chain</p>
                <p className="text-[8.5px]" style={{ color: "#64748B" }}>2 dependent transactions</p>
              </div>
              {/* Donut 52% */}
              <svg viewBox="0 0 36 36" className="w-9 h-9 shrink-0" aria-hidden="true">
                <circle cx="18" cy="18" r="15" fill="none" stroke="#F1F5F9" strokeWidth="4" />
                <circle cx="18" cy="18" r="15" fill="none" stroke="#7C3AED" strokeWidth="4"
                        strokeDasharray={`${15 * 2 * Math.PI * 0.52} ${15 * 2 * Math.PI * (1 - 0.52)}`}
                        strokeDashoffset={15 * 2 * Math.PI * 0.25} strokeLinecap="round" />
                <text x="18" y="20.5" textAnchor="middle" fontSize="8" fontWeight="700" fill="#0F172A">52%</text>
              </svg>
            </div>
          </div>

          {/* Key Dates */}
          <div className="rounded-lg p-2.5" style={{ background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.05)" }}>
            <p className="text-[7.5px] font-bold uppercase tracking-[0.12em] mb-1.5" style={{ color: "#94A3B8" }}>Key Dates</p>
            <ul className="space-y-1">
              {[
                ["Mortgage expires", "20 May 2026", "In 9 days"],
                ["Exchange target",  "24 May 2026", "In 9 days"],
                ["Completion target","18 Jun 2026", "In 34 days"],
              ].map(([k, d, rel]) => (
                <li key={k} className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 items-center">
                  <span className="min-w-0">
                    <span className="block text-[8.5px] font-bold leading-tight" style={{ color: "#0F172A" }}>{k}</span>
                    <span className="block text-[8px] tabular-nums" style={{ color: "#94A3B8" }}>{d}</span>
                  </span>
                  <span className="text-[8px] font-semibold tabular-nums" style={{ color: "#6D28D9" }}>{rel}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* AI Flow Signal */}
          <div className="rounded-lg p-2.5" style={{ background: "linear-gradient(160deg, #F5F3FF 0%, #FFFFFF 100%)", border: "1px solid rgba(124,58,237,0.16)" }}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-[7.5px] font-bold uppercase tracking-[0.12em] inline-flex items-center gap-1" style={{ color: "#6D28D9" }}>
                <Sparkles size={8} /> AI Flow Signal
              </p>
              <span className="text-[7.5px] font-bold uppercase tracking-[0.10em] px-1.5 h-[14px] inline-flex items-center rounded-full"
                    style={{ background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FECACA" }}>High</span>
            </div>
            <p className="text-[8.5px] leading-snug" style={{ color: "#475569" }}>Exchange probability reduced due to delayed enquiries and solicitor inactivity.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* iPhone Pro render — full operational app view matching reference */
const MobIPhone = () => (
  <div
    className="relative w-[230px] h-[480px] rounded-[48px] p-[9px] z-10"
    style={{
      background: "linear-gradient(180deg, #1B1D3A 0%, #0B0C1E 100%)",
      boxShadow: "0 50px 110px -30px rgba(60,40,180,0.55), 0 24px 60px -18px rgba(15,23,42,0.50), inset 0 0 0 1.5px rgba(255,255,255,0.06)",
    }}
  >
    {/* Dynamic Island */}
    <span className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[88px] h-[26px] rounded-full bg-black z-10" />
    {/* Side power button hint */}
    <span aria-hidden="true" className="absolute right-[-2px] top-[110px] w-[3px] h-[58px] rounded-l-sm" style={{ background: "linear-gradient(180deg, #2D3155, #14162B)" }} />
    <div
      className="w-full h-full rounded-[40px] overflow-hidden relative flex flex-col"
      style={{ background: "#FFFFFF" }}
    >
      {/* Status bar */}
      <div className="flex items-center justify-between px-5 pt-2 text-[8.5px] font-semibold" style={{ color: "#0F172A" }}>
        <span>9:01</span>
        <span className="flex items-center gap-1 opacity-80">
          <span className="inline-block w-2.5 h-2 rounded-[1px] bg-slate-900" />
          <span className="inline-block w-2 h-2 rounded-full bg-slate-900" />
          <span className="inline-block w-3 h-2 rounded-[2px] bg-slate-900" />
        </span>
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-hidden px-3 pt-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-1.5 min-w-0">
            <ArrowRight size={11} className="rotate-180 shrink-0 mt-0.5" style={{ color: "#64748B" }} />
            <div className="min-w-0">
              <p className="font-display text-[12.5px] font-bold leading-tight" style={{ color: "#0F172A" }}>23 King Street,<br/>Manchester</p>
              <p className="text-[8.5px] mt-0.5" style={{ color: "#94A3B8" }}>Ref: START-7264 · Sale · Freehold</p>
            </div>
          </div>
          <span className="text-[7.5px] font-bold uppercase tracking-[0.12em] px-1.5 h-[14px] inline-flex items-center rounded-full shrink-0"
                style={{ background: "#ECFDF5", color: "#047857", border: "1px solid #A7F3D0" }}>On Track</span>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-3 mt-3 pb-1.5 border-b" style={{ borderColor: "rgba(15,23,42,0.08)" }}>
          <span className="text-[9.5px] font-bold pb-1 -mb-px relative" style={{ color: "#6D28D9" }}>
            Timeline
            <span className="absolute -bottom-px left-0 right-0 h-[1.5px] rounded-full" style={{ background: "#7C3AED" }} />
          </span>
          <span className="text-[9.5px] font-semibold inline-flex items-center gap-1" style={{ color: "#94A3B8" }}>
            Updates <span className="text-[7.5px] font-bold tabular-nums">6</span>
          </span>
          <span className="text-[9.5px] font-semibold inline-flex items-center gap-1" style={{ color: "#94A3B8" }}>
            Tasks <span className="text-[7.5px] font-bold tabular-nums">3</span>
          </span>
        </div>

        {/* Next up */}
        <p className="text-[7.5px] font-bold uppercase tracking-[0.12em] mt-2.5" style={{ color: "#94A3B8" }}>Next up</p>
        <div className="mt-1 rounded-lg p-2 flex items-center gap-2"
             style={{ background: "linear-gradient(160deg, #F5F3FF 0%, #EDE9FE 100%)", border: "1px solid #DDD6FE" }}>
          <span className="w-5 h-5 rounded-md inline-flex items-center justify-center shrink-0"
                style={{ background: "#7C3AED", color: "#FFFFFF" }}>
            <MessageSquare size={10} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[9px] font-bold leading-tight" style={{ color: "#0F172A" }}>Replies to enquiries</p>
            <p className="text-[8px]" style={{ color: "#6D28D9" }}>Due in 1h</p>
          </div>
          <span className="text-[7.5px] font-bold uppercase tracking-[0.10em] px-1.5 h-[14px] inline-flex items-center rounded-full shrink-0"
                style={{ background: "#FFFFFF", color: "#6D28D9", border: "1px solid #DDD6FE" }}>High priority</span>
        </div>

        {/* Today */}
        <p className="text-[7.5px] font-bold uppercase tracking-[0.12em] mt-2.5" style={{ color: "#94A3B8" }}>Today</p>
        <ul className="mt-1 space-y-1.5">
          {[
            { time: "10:04", t: "Mortgage offer received",     sub: "James Smith, NatWest",       Icon: PoundSterling, dot: "#10B981", pill: "Completed", pillCol: "emerald" },
            { time: "09:56", t: "Searches returned",            sub: "Local Authority + Drainage", Icon: Search,        dot: "#10B981", pill: "Completed", pillCol: "emerald" },
            { time: "09:41", t: "TA6 received",                 sub: "Signed by Mark Thompson",    Icon: FileText,      dot: "#F59E0B", pill: "Completed", pillCol: "emerald" },
            { time: "09:32", t: "Draft contracts received",     sub: "Sarah Lee",                  Icon: FileText,      dot: "#10B981", pill: "Completed", pillCol: "emerald" },
          ].map((row, i) => {
            const pillStyle = row.pillCol === "emerald"
              ? { bg: "#ECFDF5", fg: "#047857", border: "#A7F3D0" }
              : { bg: "#FFFBEB", fg: "#B45309", border: "#FDE68A" };
            return (
              <li key={i} className="flex items-center gap-1.5">
                <span className="text-[7.5px] tabular-nums shrink-0 w-7" style={{ color: "#94A3B8" }}>{row.time}</span>
                <span
                  className="w-5 h-5 rounded-md inline-flex items-center justify-center shrink-0"
                  style={{ background: `${row.dot}1A`, color: row.dot }}
                >
                  <row.Icon size={9} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[9px] font-bold leading-tight truncate" style={{ color: "#0F172A" }}>{row.t}</span>
                  <span className="block text-[7.5px] truncate" style={{ color: "#94A3B8" }}>{row.sub}</span>
                </span>
                <span className="text-[7px] font-bold uppercase tracking-[0.10em] px-1 h-[12px] inline-flex items-center rounded-full shrink-0"
                      style={{ background: pillStyle.bg, color: pillStyle.fg, border: `1px solid ${pillStyle.border}` }}>{row.pill}</span>
              </li>
            );
          })}
        </ul>

        {/* Yesterday */}
        <p className="text-[7.5px] font-bold uppercase tracking-[0.12em] mt-2.5" style={{ color: "#94A3B8" }}>Yesterday</p>
        <ul className="mt-1 space-y-1.5">
          <li className="flex items-center gap-1.5">
            <span className="text-[7.5px] tabular-nums shrink-0 w-7" style={{ color: "#94A3B8" }}>16:22</span>
            <span className="w-5 h-5 rounded-md inline-flex items-center justify-center shrink-0"
                  style={{ background: "#FFFBEB", color: "#B45309" }}>
              <AlertTriangle size={9} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[9px] font-bold leading-tight truncate" style={{ color: "#0F172A" }}>Solicitor awaiting response</span>
              <span className="block text-[7.5px] truncate" style={{ color: "#94A3B8" }}>Enquiries (Q4)</span>
            </span>
            <span className="text-[7px] font-bold uppercase tracking-[0.10em] px-1 h-[12px] inline-flex items-center rounded-full shrink-0"
                  style={{ background: "#FFFBEB", color: "#B45309", border: "1px solid #FDE68A" }}>Waiting</span>
          </li>
        </ul>
      </div>

      {/* Bottom tab bar */}
      <div className="flex items-center justify-around px-2 py-2 border-t" style={{ borderColor: "rgba(15,23,42,0.06)", background: "#FAFAFD" }}>
        {[
          { Icon: Home,         label: "Deal Room",  active: true,  badge: null },
          { Icon: FileText,     label: "Documents",  active: false, badge: null },
          { Icon: MessageSquare,label: "Messages",   active: false, badge: 2 },
          { Icon: Bell,         label: "Alerts",     active: false, badge: 1, badgeRed: true },
          { Icon: Plus,         label: "More",       active: false, badge: null },
        ].map((tab) => (
          <span key={tab.label} className="relative flex flex-col items-center gap-0.5">
            <tab.Icon size={13} style={{ color: tab.active ? "#7C3AED" : "#94A3B8" }} />
            <span className="text-[7px] font-semibold" style={{ color: tab.active ? "#6D28D9" : "#94A3B8" }}>{tab.label}</span>
            {tab.badge && (
              <span className="absolute -top-1 right-0 w-3 h-3 rounded-full text-[7px] font-bold inline-flex items-center justify-center text-white"
                    style={{ background: tab.badgeRed ? "#EF4444" : "#7C3AED" }}>
                {tab.badge}
              </span>
            )}
          </span>
        ))}
      </div>
    </div>
  </div>
);

const MobileContinuity = () => (
  <section
    className="relative max-w-[1320px] mx-auto px-6 lg:px-10 py-20 lg:py-24 overflow-hidden"
    data-testid="landing-continuity"
  >
    {/* Ambient cinematic glow */}
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
      <div className="absolute right-[6%] top-[18%] w-[460px] h-[460px] rounded-full"
           style={{ background: "radial-gradient(circle, rgba(124,58,237,0.22) 0%, rgba(124,58,237,0) 65%)", filter: "blur(20px)" }} />
      <div className="absolute right-[30%] bottom-[8%] w-[360px] h-[360px] rounded-full"
           style={{ background: "radial-gradient(circle, rgba(167,139,250,0.18) 0%, rgba(167,139,250,0) 70%)", filter: "blur(28px)" }} />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] gap-12 lg:gap-16 items-center">
      {/* ── Left: editorial copy + benefit stack ────────────────────── */}
      <div>
        <p className="text-[10.5px] font-bold uppercase tracking-[0.22em]" style={{ color: "#6D28D9" }}>
          <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle cf-pulse" style={{ background: "#7C3AED" }} />
          Built for the way agents work
        </p>
        <h2
          className="font-display text-[36px] sm:text-[44px] lg:text-[52px] font-bold tracking-[-0.028em] leading-[1.02] mt-4"
          style={{ color: "#0F172A" }}
          data-testid="continuity-headline"
        >
          Transactions don't stop when{" "}
          <span style={{
            background: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 50%, #4F46E5 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}>
            agents leave the office.
          </span>
        </h2>
        <p className="text-[14.5px] leading-relaxed mt-5 max-w-[480px]" style={{ color: "#475569" }}>
          Full operational visibility across every device. Keep deals moving, respond faster
          and stay in control between viewings.
        </p>

        {/* Benefit stack */}
        <div className="mt-8 space-y-5 max-w-[480px]" data-testid="continuity-benefits">
          {MOB_BENEFITS.map(({ Icon, title, sub }) => (
            <div key={title} className="flex items-start gap-3.5">
              <span
                className="w-10 h-10 rounded-xl inline-flex items-center justify-center shrink-0"
                style={{
                  background: "linear-gradient(135deg, #F5F3FF 0%, #FFFFFF 100%)",
                  border: "1px solid rgba(124,58,237,0.18)",
                  color: "#6D28D9",
                  boxShadow: "0 6px 14px -8px rgba(124,58,237,0.18), inset 0 1px 0 rgba(255,255,255,0.8)",
                }}
              >
                <Icon size={15} strokeWidth={2.1} />
              </span>
              <div className="min-w-0">
                <p className="font-display text-[14.5px] font-bold leading-tight" style={{ color: "#0F172A" }}>{title}</p>
                <p className="text-[12.5px] mt-1 leading-snug" style={{ color: "#64748B" }}>{sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Subtle availability row — 4 monochrome icons + caption */}
        <div className="mt-10" data-testid="continuity-availability">
          <div className="flex items-center gap-3 mb-2">
            {[Apple, Smartphone, Tablet, Monitor].map((Icon, i) => (
              <span key={i} className="inline-flex items-center justify-center" style={{ color: "#475569" }}>
                <Icon size={20} strokeWidth={1.8} />
              </span>
            ))}
          </div>
          <p className="text-[12px]" style={{ color: "#64748B" }}>
            Available on iPhone, Android,<br className="hidden sm:inline" /> iPad and desktop.
          </p>
        </div>
      </div>

      {/* ── Right: cinematic device composition · iPhone LEFT, iPad RIGHT ─ */}
      <div className="relative h-[600px] lg:h-[660px] flex items-center justify-center">
        {/* Atmospheric base glow beneath devices */}
        <div
          aria-hidden="true"
          className="absolute left-1/2 -translate-x-1/2 bottom-6 w-[78%] h-[60px] rounded-full opacity-60"
          style={{ background: "radial-gradient(ellipse at center, rgba(124,58,237,0.40) 0%, rgba(124,58,237,0) 65%)", filter: "blur(24px)" }}
        />

        {/* Layered devices — iPhone in front (left), iPad behind (right) */}
        <div className="relative flex items-end justify-center">
          {/* iPad sits behind, offset to the right */}
          <div className="hidden sm:block transform translate-x-[20px] translate-y-[14px] origin-bottom-left">
            <MobIPad />
          </div>
          {/* iPhone in front, offset slightly to the left */}
          <div className="transform sm:-translate-x-[140px] lg:-translate-x-[180px] -translate-y-[6px] origin-bottom-right">
            <MobIPhone />
          </div>
        </div>

        {/* Floating operational cards (5 total) */}
        {MOB_FLOAT_CARDS.map((c) => (
          <MobFloatingCard key={c.id} {...c} />
        ))}
      </div>
    </div>

    {/* ── Operational Intelligence — bottom rich KPI section ─────────── */}
    <div className="mt-16 lg:mt-24 grid grid-cols-1 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,2.15fr)] gap-10 lg:gap-12 items-start" data-testid="continuity-kpis">
      {/* Left text */}
      <div>
        <p className="text-[10.5px] font-bold uppercase tracking-[0.22em]" style={{ color: "#6D28D9" }}>
          <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle cf-pulse" style={{ background: "#7C3AED" }} />
          Operational Intelligence
        </p>
        <h3 className="font-display text-[28px] sm:text-[32px] lg:text-[36px] font-bold tracking-[-0.024em] leading-[1.05] mt-3" style={{ color: "#0F172A" }}>
          See what matters.<br/>Act before deals stall.
        </h3>
        <p className="text-[13.5px] leading-relaxed mt-4 max-w-[420px]" style={{ color: "#475569" }}>
          Instant visibility into transaction certainty, deal health, risk and momentum — built on the same intelligence layer that powers every Deal Room.
        </p>
      </div>

      {/* 4 rich KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3.5">
        {/* Deal health */}
        <div className="rounded-2xl bg-white p-4 hover:-translate-y-0.5 transition-transform"
             style={{ border: "1px solid rgba(15,23,42,0.06)", boxShadow: "0 24px 50px -28px rgba(15,23,42,0.18), 0 6px 14px -8px rgba(15,23,42,0.06)" }}
             data-testid="continuity-kpi-deal-health">
          <p className="text-[11px] font-bold" style={{ color: "#475569" }}>Deal health</p>
          <div className="mt-2.5 flex items-center gap-3">
            <svg viewBox="0 0 42 42" className="w-[46px] h-[46px] shrink-0" aria-hidden="true">
              <circle cx="21" cy="21" r="17" fill="none" stroke="#ECFDF5" strokeWidth="5" />
              <circle cx="21" cy="21" r="17" fill="none" stroke="#10B981" strokeWidth="5"
                      strokeDasharray={`${17 * 2 * Math.PI * 0.82} ${17 * 2 * Math.PI * 0.18}`}
                      strokeDashoffset={17 * 2 * Math.PI * 0.25} strokeLinecap="round" />
              <text x="21" y="24" textAnchor="middle" fontSize="11" fontWeight="700" fill="#0F172A">82</text>
            </svg>
            <span className="text-[10.5px] font-bold uppercase tracking-[0.10em] px-2 h-[20px] inline-flex items-center rounded-full"
                  style={{ background: "#ECFDF5", color: "#047857", border: "1px solid #A7F3D0" }}>Good</span>
          </div>
          <p className="text-[10.5px] mt-2.5 inline-flex items-center gap-1" style={{ color: "#047857" }}>
            Up 6% this week
            <TrendingUp size={11} />
          </p>
          <div className="mt-2">
            <MobMiniSpark points={[58, 62, 65, 70, 72, 78, 82]} stroke="#10B981" />
          </div>
        </div>

        {/* Exchange probability */}
        <div className="rounded-2xl bg-white p-4 hover:-translate-y-0.5 transition-transform"
             style={{ border: "1px solid rgba(15,23,42,0.06)", boxShadow: "0 24px 50px -28px rgba(15,23,42,0.18), 0 6px 14px -8px rgba(15,23,42,0.06)" }}
             data-testid="continuity-kpi-exchange-probability">
          <p className="text-[11px] font-bold" style={{ color: "#475569" }}>Exchange probability</p>
          <div className="mt-2.5 flex items-center gap-2">
            <p className="font-display text-[28px] font-bold tabular-nums leading-none" style={{ color: "#6D28D9" }}>78<span className="text-[16px]">%</span></p>
            <span className="text-[10.5px] font-bold uppercase tracking-[0.10em] px-2 h-[20px] inline-flex items-center rounded-full"
                  style={{ background: "#F5F3FF", color: "#6D28D9", border: "1px solid #DDD6FE" }}>High</span>
          </div>
          <p className="text-[10.5px] mt-2.5 inline-flex items-center gap-1" style={{ color: "#B91C1C" }}>
            Down 5% from last week
            <TrendingDown size={11} />
          </p>
          <div className="mt-2">
            <MobMiniSpark points={[68, 72, 75, 80, 82, 80, 78]} stroke="#7C3AED" />
          </div>
        </div>

        {/* Active risks */}
        <div className="rounded-2xl bg-white p-4 hover:-translate-y-0.5 transition-transform"
             style={{ border: "1px solid rgba(15,23,42,0.06)", boxShadow: "0 24px 50px -28px rgba(15,23,42,0.18), 0 6px 14px -8px rgba(15,23,42,0.06)" }}
             data-testid="continuity-kpi-active-risks">
          <p className="text-[11px] font-bold" style={{ color: "#475569" }}>Active risks</p>
          <div className="mt-2.5 flex items-center gap-2">
            <p className="font-display text-[28px] font-bold tabular-nums leading-none" style={{ color: "#B91C1C" }}>2</p>
            <span className="text-[10.5px] font-bold uppercase tracking-[0.10em] px-2 h-[20px] inline-flex items-center rounded-full"
                  style={{ background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FECACA" }}>High priority</span>
          </div>
          <p className="text-[10.5px] mt-2.5 inline-flex items-center gap-1" style={{ color: "#B91C1C" }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#EF4444" }} />
            1 new today
            <TrendingDown size={11} />
          </p>
          <div className="mt-2">
            <MobMiniSpark points={[4, 3, 3, 2, 2, 2, 2]} stroke="#EF4444" />
          </div>
        </div>

        {/* Chain visibility */}
        <div className="rounded-2xl bg-white p-4 hover:-translate-y-0.5 transition-transform"
             style={{ border: "1px solid rgba(15,23,42,0.06)", boxShadow: "0 24px 50px -28px rgba(15,23,42,0.18), 0 6px 14px -8px rgba(15,23,42,0.06)" }}
             data-testid="continuity-kpi-chain-visibility">
          <div className="flex items-start justify-between">
            <p className="text-[11px] font-bold" style={{ color: "#475569" }}>Chain visibility</p>
            {/* Chain node graphic */}
            <svg viewBox="0 0 36 18" className="w-9 h-[18px] shrink-0" aria-hidden="true">
              <line x1="6" y1="9" x2="18" y2="9" stroke="#7C3AED" strokeWidth="1.5" />
              <line x1="18" y1="9" x2="30" y2="9" stroke="#7C3AED" strokeWidth="1.5" />
              <circle cx="6" cy="9" r="3.5" fill="#7C3AED" />
              <circle cx="18" cy="9" r="3.5" fill="#7C3AED" />
              <circle cx="30" cy="9" r="3.5" fill="#7C3AED" />
            </svg>
          </div>
          <p className="font-display text-[28px] font-bold tabular-nums leading-none mt-2.5" style={{ color: "#0F172A" }}>
            3<span style={{ color: "#94A3B8" }}>/4</span>
          </p>
          <p className="text-[10.5px] mt-2.5 font-semibold" style={{ color: "#047857" }}>Links on track</p>
          <p className="text-[10.5px] mt-1" style={{ color: "#94A3B8" }}>2 dependent transactions</p>
        </div>
      </div>
    </div>
  </section>
);

/* ─── Operational intelligence ───────────────────────── */

/* Subtle count-up — single rAF easing, restrained and infrastructure-grade. */
const useCountUp = (target, { duration = 900, delay = 120 } = {}) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf;
    let start;
    const t = setTimeout(() => {
      const step = (ts) => {
        if (!start) start = ts;
        const p = Math.min(1, (ts - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
        setV(Math.round(target * eased));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => { clearTimeout(t); cancelAnimationFrame(raf); };
  }, [target, duration, delay]);
  return v;
};

/* 7-day exchange probability sparkline — area + line + dots + gridlines. */
const EXCHANGE_TREND = [
  ["May 10", 32],
  ["May 11", 44],
  ["May 12", 45],
  ["May 13", 58],
  ["May 14", 70],
  ["May 15", 77],
  ["May 16", 87],
];

const ExchangeTrendChart = () => {
  const W = 560;
  const H = 220;
  const padL = 12;
  const padR = 46;
  const padT = 18;
  const padB = 30;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const points = EXCHANGE_TREND.map(([d, v], i) => {
    const x = padL + (innerW * i) / (EXCHANGE_TREND.length - 1);
    const y = padT + innerH * (1 - v / 100);
    return { x, y, d, v };
  });
  const lineD = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  const areaD = `${lineD} L ${points[points.length - 1].x.toFixed(1)} ${(padT + innerH).toFixed(1)} L ${points[0].x.toFixed(1)} ${(padT + innerH).toFixed(1)} Z`;
  const yTicks = [100, 75, 50, 25, 0];
  const last = points[points.length - 1];

  return (
    <div className="relative w-full" style={{ aspectRatio: `${W} / ${H}` }}>
      <svg viewBox={`0 0 ${W} ${H}`} className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="cf-exch-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor="#10B981" stopOpacity="0.26" />
            <stop offset="60%" stopColor="#10B981" stopOpacity="0.10" />
            <stop offset="100%" stopColor="#10B981" stopOpacity="0.00" />
          </linearGradient>
          {/* Soft outer glow for the trend line */}
          <filter id="cf-line-glow" x="-10%" y="-10%" width="120%" height="120%">
            <feGaussianBlur stdDeviation="1.6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Horizontal gridlines — slightly lighter for calmer look */}
        {yTicks.map((t) => {
          const y = padT + innerH * (1 - t / 100);
          return (
            <line
              key={t}
              x1={padL} x2={W - padR}
              y1={y} y2={y}
              stroke="#EEF2F7"
              strokeWidth="1"
              strokeDasharray={t === 0 ? "0" : "2 4"}
              opacity={t === 0 ? 1 : 0.85}
            />
          );
        })}

        {/* Soft green area */}
        <path d={areaD} fill="url(#cf-exch-fill)" className="cf-area-in" />

        {/* Trend line — animated draw on mount */}
        <path
          d={lineD}
          fill="none"
          stroke="#10B981"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#cf-line-glow)"
          className="cf-line-draw"
          pathLength="1"
        />

        {/* Earlier dots */}
        {points.slice(0, -1).map((p, i) => (
          <circle key={`dot-${i}`} cx={p.x} cy={p.y} r="4.2" fill="#fff" stroke="#10B981" strokeWidth="2" />
        ))}

        {/* Latest point — slightly larger, soft pulse halo */}
        <g>
          <circle cx={last.x} cy={last.y} r="9" fill="#10B981" opacity="0.18" className="cf-pulse-halo" />
          <circle cx={last.x} cy={last.y} r="5.6" fill="#10B981" />
          <circle cx={last.x} cy={last.y} r="2.6" fill="#fff" />
        </g>
      </svg>

      {/* HTML overlay: percentage + date labels (crisper than SVG <text>) */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {yTicks.map((t) => {
          const yPct = ((padT + innerH * (1 - t / 100)) / H) * 100;
          return (
            <span
              key={`ylab-${t}`}
              className="absolute text-[10.5px] font-semibold text-slate-400 tabular-nums"
              style={{ top: `calc(${yPct}% - 7px)`, right: 4, lineHeight: 1 }}
            >
              {t}%
            </span>
          );
        })}
        {points.map((p, i) => {
          const xPct = (p.x / W) * 100;
          return (
            <span
              key={`xlab-${i}`}
              className="absolute text-[10.5px] font-semibold text-slate-500 whitespace-nowrap"
              style={{ bottom: 2, left: `${xPct}%`, transform: "translateX(-50%)", lineHeight: 1 }}
            >
              {p.d}
            </span>
          );
        })}
      </div>

      {/* Restrained, infrastructure-grade motion */}
      <style>{`
        .cf-line-draw {
          stroke-dasharray: 1;
          stroke-dashoffset: 1;
          animation: cf-draw 1100ms cubic-bezier(0.22, 1, 0.36, 1) 120ms forwards;
        }
        @keyframes cf-draw { to { stroke-dashoffset: 0; } }
        .cf-area-in { opacity: 0; animation: cf-fade 700ms ease-out 700ms forwards; }
        @keyframes cf-fade { to { opacity: 1; } }
        .cf-pulse-halo { transform-box: fill-box; transform-origin: center; animation: cf-pulse 2400ms ease-in-out 1.4s infinite; }
        @keyframes cf-pulse {
          0%, 100% { transform: scale(1);   opacity: 0.18; }
          50%      { transform: scale(1.6); opacity: 0.02; }
        }
        @media (prefers-reduced-motion: reduce) {
          .cf-line-draw, .cf-area-in, .cf-pulse-halo { animation: none !important; }
          .cf-line-draw { stroke-dashoffset: 0; }
          .cf-area-in { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

/* Compact 5-row "health breakdown" — icon · label · progress · status.
   Thinner, muted-tone bars to reduce dominance against the score ring. */
const HealthBreakdownRow = ({ icon: Icon, iconBg, iconFg, label, score, status, statusColor, delay = 0 }) => (
  <div className="grid grid-cols-[auto_minmax(0,1fr)_minmax(64px,1fr)_auto] items-center gap-3 py-1.5">
    <span
      className="w-6 h-6 rounded-md inline-flex items-center justify-center shrink-0"
      style={{ background: iconBg, color: iconFg }}
    >
      <Icon size={12} />
    </span>
    <span className="text-[12.5px] font-semibold text-slate-800 truncate">{label}</span>
    <div className="relative h-1 rounded-full overflow-hidden" style={{ background: "#EEF2F7" }}>
      <span
        className="absolute inset-y-0 left-0 rounded-full cf-bar-settle"
        style={{
          width: `${Math.max(8, Math.min(100, score))}%`,
          background: statusColor,
          opacity: 0.55,
          animationDelay: `${delay}ms`,
        }}
      />
    </div>
    <span className="text-[11px] font-bold whitespace-nowrap shrink-0" style={{ color: statusColor }}>
      {status}
    </span>
  </div>
);

const OperationalIntelligence = () => {
  // Subtle count-up for the Deal Health score
  const animatedScore = useCountUp(82, { duration: 1100, delay: 180 });

  return (
  <section className="max-w-[1320px] mx-auto px-6 lg:px-10 pb-16" data-testid="landing-intelligence">
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,0.7fr)_minmax(0,2fr)] gap-8 items-start">
      {/* Left intro column — unchanged */}
      <div className="pt-3">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-violet-700">Operational intelligence</p>
        <h2 className="font-display text-[32px] lg:text-[38px] font-bold tracking-[-0.022em] text-slate-900 leading-[1.06] mt-3">
          See what matters.<br/>Act before deals stall.
        </h2>
        <p className="text-[13.5px] text-slate-600 leading-relaxed mt-4 max-w-[420px]">
          Instant visibility into transaction certainty, deal health, risk and momentum —
          built on the same intelligence layer that powers every Deal Room.
        </p>
        <Link to="/login" className="inline-flex items-center gap-1.5 mt-5 text-[13px] font-bold text-violet-700">
          Explore intelligence <ArrowRight size={13} />
        </Link>
      </div>

      {/* Right column — two premium stacked cards */}
      <div className="space-y-5">

        {/* ─── Card 1 · Exchange Probability ─── */}
        <SoftCard className="p-5 lg:p-7" data-testid="intel-card-exchange">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span
                className="w-10 h-10 rounded-xl inline-flex items-center justify-center shrink-0"
                style={{ background: "#ECFDF5", border: "1px solid #D1FAE5", color: "#10B981" }}
              >
                <ArrowLeftRight size={16} strokeWidth={2.4} />
              </span>
              <p className="text-[11.5px] font-bold uppercase tracking-[0.18em] text-slate-500">
                Exchange Probability
              </p>
            </div>
            <button
              type="button"
              className="h-9 px-3 rounded-lg text-[12px] font-semibold text-slate-700 bg-white inline-flex items-center gap-1.5 hover:border-slate-300"
              style={{ border: "1px solid #E2E8F0", boxShadow: "0 1px 0 rgba(15,23,42,0.02)" }}
              data-testid="intel-exchange-range"
            >
              Last 7 days <ChevronDown size={12} className="text-slate-400" />
            </button>
          </div>

          {/* Body — left metric column + right chart */}
          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,0.85fr)_minmax(0,1.5fr)] gap-5 md:gap-6 mt-5 items-start">
            <div>
              <p
                className="font-display font-bold tracking-[-0.030em] text-slate-900 tabular-nums leading-none text-[56px] md:text-[68px]"
                data-testid="intel-exchange-metric"
              >
                87%
              </p>
              <div className="mt-3 inline-flex items-center gap-2">
                <span
                  className="inline-flex items-center gap-1 px-2 h-6 rounded-md text-[11.5px] font-bold tabular-nums"
                  style={{ background: "#ECFDF5", border: "1px solid #D1FAE5", color: "#059669" }}
                >
                  <TrendingUp size={11} strokeWidth={2.6} />
                  11%
                </span>
                <span className="text-[11.5px] text-slate-500">vs last 7 days</span>
              </div>

              {/* (1) Contextual intelligence — WHY IT IMPROVED */}
              <div
                className="mt-4 rounded-xl pl-3 pr-3.5 py-3 relative"
                style={{
                  background: "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
                  border: "1px solid #E2E8F0",
                }}
                data-testid="intel-why-improved"
              >
                {/* subtle left accent line */}
                <span
                  aria-hidden="true"
                  className="absolute left-0 top-2 bottom-2 w-[2px] rounded-full"
                  style={{ background: "linear-gradient(180deg, #10B981, #34D399)" }}
                />
                <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-slate-500">
                  Why it improved
                </p>
                <ul className="mt-2 space-y-1.5">
                  {[
                    "Mortgage offer received",
                    "Buyer AML verified",
                    "Seller solicitor active again",
                  ].map((t) => (
                    <li key={t} className="text-[12px] text-slate-700 inline-flex items-start gap-1.5 leading-snug">
                      <Check size={11} strokeWidth={2.8} className="text-emerald-600 mt-[2px] shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* (3) Strong chance + balanced risk signal */}
              <p className="font-display text-[15px] md:text-[16px] font-bold text-slate-900 mt-4 leading-tight">
                Strong chance of exchange
              </p>
              <div
                className="mt-2 inline-flex items-start gap-2 px-2.5 py-1.5 rounded-md text-[11.5px] leading-snug"
                style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E" }}
                data-testid="intel-risk-signal"
                role="note"
              >
                <AlertTriangle size={12} strokeWidth={2.4} className="mt-[2px] shrink-0" />
                <span>
                  <span className="font-semibold">Local authority searches</span> still outstanding.
                </span>
              </div>
            </div>

            <div className="min-w-0">
              <ExchangeTrendChart />

              {/* (2) Live operational metadata — calm awareness, not notifications */}
              <div
                className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10.5px] text-slate-400 font-medium"
                data-testid="intel-live-meta"
              >
                <span className="inline-flex items-center gap-1.5">
                  <span className="relative inline-flex w-1.5 h-1.5">
                    <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-50" />
                    <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  </span>
                  Updated 2 mins ago
                </span>
                <span className="text-slate-300">·</span>
                <span>Awaiting searches — 18h</span>
                <span className="text-slate-300">·</span>
                <span>Mortgage expires in 11 days</span>
                <span className="text-slate-300">·</span>
                <span>Last solicitor response — 43m</span>
              </div>
            </div>
          </div>
        </SoftCard>

        {/* ─── Card 2 · Deal Health ─── */}
        <SoftCard className="p-5 lg:p-7" data-testid="intel-card-health">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span
                className="w-10 h-10 rounded-xl inline-flex items-center justify-center shrink-0"
                style={{ background: "#ECFDF5", border: "1px solid #D1FAE5", color: "#10B981" }}
              >
                <Heart size={16} strokeWidth={2.2} />
              </span>
              <p className="text-[11.5px] font-bold uppercase tracking-[0.18em] text-slate-500">
                Deal Health
              </p>
            </div>
            <Link
              to="/login"
              className="h-9 px-3 rounded-lg text-[12px] font-semibold text-slate-700 bg-white inline-flex items-center gap-1.5 hover:border-slate-300"
              style={{ border: "1px solid #E2E8F0", boxShadow: "0 1px 0 rgba(15,23,42,0.02)" }}
              data-testid="intel-health-report"
            >
              View full report <ArrowRight size={12} />
            </Link>
          </div>

          {/* Body — restructured hierarchy:
              1) Score ring  2) "Good"  3) Exchange readiness statement  4) Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,0.95fr)_minmax(0,1.2fr)] gap-6 lg:gap-8 mt-5 items-center">
            {/* Score block */}
            <div className="flex items-center gap-5">
              <div className="relative w-[124px] h-[124px] shrink-0">
                <svg viewBox="0 0 36 36" className="w-[124px] h-[124px] -rotate-90">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#EEF2F7" strokeWidth="3"/>
                  <circle
                    cx="18" cy="18" r="15.9"
                    fill="none" stroke="#10B981" strokeWidth="3"
                    strokeDasharray={`${animatedScore},100`} strokeLinecap="round"
                    style={{ transition: "stroke-dasharray 80ms linear" }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span
                    className="font-display text-[30px] font-bold text-slate-900 tabular-nums leading-none"
                    data-testid="intel-health-score"
                  >
                    {animatedScore}
                  </span>
                  <span className="text-[10.5px] font-semibold text-slate-400 mt-1 tabular-nums">/100</span>
                </div>
              </div>
              <div className="min-w-0">
                {/* (5) Emphasised hierarchy: Good (larger) + On track for exchange (prominent) */}
                <div className="flex items-center gap-2">
                  <span className="font-display text-[24px] md:text-[26px] font-bold text-emerald-600 leading-none">Good</span>
                  <span
                    className="w-6 h-6 rounded-full inline-flex items-center justify-center"
                    style={{ background: "#ECFDF5", color: "#10B981" }}
                  >
                    <TrendingUp size={11} strokeWidth={2.6} />
                  </span>
                </div>
                <p className="font-display text-[14px] md:text-[15px] font-bold text-slate-900 mt-2 leading-tight">
                  On track for exchange
                </p>
                <p className="text-[11.5px] text-slate-500 mt-1.5 inline-flex items-center gap-1.5">
                  <ShieldCheck size={11} className="text-emerald-500" />
                  No critical issues
                </p>
              </div>
            </div>

            {/* (6) Renamed breakdown with operational transaction language */}
            <div data-testid="intel-health-breakdown" className="min-w-0">
              <HealthBreakdownRow
                icon={Users}        iconBg="#ECFDF5" iconFg="#10B981"
                label="Parties"     score={82} status="Good"     statusColor="#10B981"
                delay={120}
              />
              <HealthBreakdownRow
                icon={FileText}     iconBg="#ECFDF5" iconFg="#10B981"
                label="Legal Pack"  score={78} status="Good"     statusColor="#10B981"
                delay={200}
              />
              <HealthBreakdownRow
                icon={Clock}        iconBg="#FFFBEB" iconFg="#F59E0B"
                label="Chain Progress" score={48} status="At risk"  statusColor="#F59E0B"
                delay={280}
              />
              <HealthBreakdownRow
                icon={MessageSquare} iconBg="#F5F3FF" iconFg="#7C3AED"
                label="Participant Response" score={86} status="Good"   statusColor="#10B981"
                delay={360}
              />
              <HealthBreakdownRow
                icon={Shield}       iconBg="#EFF6FF" iconFg="#3B82F6"
                label="Risk Exposure" score={88} status="Low"     statusColor="#10B981"
                delay={440}
              />
            </div>
          </div>

          {/* Bottom insight banner */}
          <div
            className="mt-4 lg:mt-5 rounded-xl px-4 py-3 flex items-center gap-3"
            style={{ background: "#ECFDF5", border: "1px solid #D1FAE5" }}
            data-testid="intel-health-insight"
          >
            <span
              className="w-8 h-8 rounded-lg inline-flex items-center justify-center shrink-0"
              style={{ background: "#FFFFFF", color: "#10B981", border: "1px solid #D1FAE5" }}
            >
              <TrendingUp size={13} strokeWidth={2.4} />
            </span>
            <p className="text-[12.5px] leading-snug">
              <span className="font-bold text-slate-900">Improving overall</span>
              <span className="text-slate-600"> — Deal health score has increased </span>
              <span className="font-bold text-slate-900 tabular-nums">9 points</span>
              <span className="text-slate-600"> vs last 7 days.</span>
            </p>
          </div>
        </SoftCard>
      </div>
    </div>

    {/* Shared restrained-motion keyframes for this section */}
    <style>{`
      .cf-bar-settle {
        transform-origin: left center;
        transform: scaleX(0);
        animation: cf-bar-in 900ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
      }
      @keyframes cf-bar-in { to { transform: scaleX(1); } }
      @media (prefers-reduced-motion: reduce) {
        .cf-bar-settle { animation: none !important; transform: scaleX(1); }
      }
    `}</style>
  </section>
  );
};

/* ─── Pipeline Exposure Audit · Elite multi-step operational audit ──── */
// Numeric controls use the brand-level PremiumStepper (built on the
// useHoldRepeat operational primitive). Keep all numeric interactions
// platform-wide on that single primitive so timing, acceleration, and
// touch parity stay identical across ChainFlow OS.

const fmtMoney = (n) => {
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `£${(n / 1_000).toLocaleString(undefined, { maximumFractionDigits: 1 })}K`;
  return `£${Math.round(n).toLocaleString()}`;
};

const ANALYSIS_LINES = [
  "Reviewing operational transaction flow",
  "Calculating exchange exposure and momentum drag",
  "Detecting solicitor and document leakage points",
  "Benchmarking against high-performing UK agencies",
  "Compiling consultancy-grade audit report",
];

const AnalysisOverlay = ({ onDone }) => {
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (active >= ANALYSIS_LINES.length) {
      const t = setTimeout(onDone, 700);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setActive((a) => a + 1), 920);
    return () => clearTimeout(t);
  }, [active, onDone]);

  return (
    <div
      className="absolute inset-0 rounded-2xl flex items-center justify-center z-10"
      style={{
        background: "rgba(255,255,255,0.78)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
      data-testid="audit-analysing"
    >
      <div className="w-full max-w-[480px] px-8">
        <div className="flex items-center gap-2 mb-5">
          <span className="relative w-9 h-9 rounded-xl inline-flex items-center justify-center"
                style={{
                  background: "linear-gradient(135deg, rgba(99,102,241,0.14), rgba(139,92,246,0.10))",
                  border: "1px solid rgba(124,58,237,0.18)",
                }}>
            <Sparkles size={15} className="text-violet-700" />
            <span className="absolute inset-0 rounded-xl"
                  style={{ boxShadow: "0 0 0 0 rgba(124,58,237,0.28)", animation: "cf-pulse 2.4s ease-out infinite" }} />
          </span>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-violet-700">Analysing transaction flow</p>
        </div>
        <ul className="space-y-3">
          {ANALYSIS_LINES.map((line, i) => {
            const done = i < active;
            const live = i === active;
            return (
              <li key={line} className="flex items-center gap-3 transition-opacity duration-500"
                  style={{ opacity: i <= active ? 1 : 0.35 }}>
                <span className="w-5 h-5 rounded-full inline-flex items-center justify-center shrink-0"
                      style={{
                        background: done ? "#10B981" : live ? "rgba(124,58,237,0.10)" : "rgba(15,23,42,0.04)",
                        border: done ? "none" : live ? "1px solid rgba(124,58,237,0.30)" : "1px solid rgba(15,23,42,0.08)",
                      }}>
                  {done ? <Check size={11} strokeWidth={3} className="text-white" />
                        : live ? <Loader2 size={11} className="text-violet-700 animate-spin" />
                        : <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />}
                </span>
                <span className={`text-[13px] ${done ? "text-slate-500" : live ? "text-slate-900 font-semibold" : "text-slate-500"}`}>
                  {line}
                </span>
              </li>
            );
          })}
        </ul>
        <div className="mt-7 h-[3px] rounded-full overflow-hidden bg-slate-100">
          <div
            className="h-full rounded-full"
            style={{
              width: `${Math.min(100, (active / ANALYSIS_LINES.length) * 100)}%`,
              background: "linear-gradient(90deg, #4F46E5, #7C3AED)",
              transition: "width 700ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          />
        </div>
      </div>
      <style>{`
        @keyframes cf-pulse {
          0% { box-shadow: 0 0 0 0 rgba(124,58,237,0.32); }
          70% { box-shadow: 0 0 0 14px rgba(124,58,237,0); }
          100% { box-shadow: 0 0 0 0 rgba(124,58,237,0); }
        }
      `}</style>
    </div>
  );
};

const LeadCaptureModal = ({ open, onClose, onSubmit, form, setForm, submitting, error, headlineLeakage }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;
  const setV = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-3 sm:p-6"
      style={{ background: "rgba(8,9,27,0.62)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
      role="dialog"
      aria-modal="true"
      data-testid="audit-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[520px] rounded-2xl overflow-hidden"
        style={{
          background: "linear-gradient(160deg, rgba(255,255,255,0.96) 0%, rgba(248,250,255,0.96) 100%)",
          border: "1px solid rgba(15,23,42,0.08)",
          boxShadow: "0 50px 90px -20px rgba(15,23,42,0.45), 0 0 0 1px rgba(124,58,237,0.08)",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full inline-flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100"
          data-testid="audit-modal-close"
        >
          <X size={16} />
        </button>

        <div className="px-7 pt-7 pb-5">
          <div className="inline-flex items-center gap-1.5 px-2.5 h-[22px] rounded-full"
               style={{ background: "rgba(124,58,237,0.10)", border: "1px solid rgba(124,58,237,0.18)" }}>
            <Lock size={10} className="text-violet-700" />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-violet-700">Unlock full audit</span>
          </div>
          <h3 className="font-display text-[22px] sm:text-[24px] font-bold tracking-[-0.012em] text-slate-900 leading-[1.15] mt-3">
            Receive your full operational audit
          </h3>
          <p className="text-[13px] text-slate-600 leading-relaxed mt-2">
            Your consultancy-grade report — including performance tier, top exposure breakdown and peer benchmark
            {headlineLeakage ? <> against <b className="text-slate-900">{headlineLeakage}</b> of exposure</> : null} — will be revealed instantly and emailed for your records.
          </p>
        </div>

        <form onSubmit={onSubmit} className="px-7 pb-7" data-testid="audit-modal-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Full name" required>
              <PremiumInput
                type="text"
                value={form.name}
                onChange={setV("name")}
                placeholder="Emma Parker"
                autoComplete="name"
                required
                testid="audit-field-name"
              />
            </Field>
            <Field label="Agency" required>
              <PremiumInput
                type="text"
                value={form.agency}
                onChange={setV("agency")}
                placeholder="Parker & Co"
                autoComplete="organization"
                required
                testid="audit-field-agency"
              />
            </Field>
            <Field label="Work email" required>
              <PremiumInput
                type="email"
                value={form.email}
                onChange={setV("email")}
                placeholder="emma@parkerco.com"
                autoComplete="email"
                required
                testid="audit-field-email"
              />
            </Field>
            <Field label="Phone">
              <PremiumInput
                type="tel"
                value={form.phone}
                onChange={setV("phone")}
                placeholder="+44 7700 900123"
                autoComplete="tel"
                testid="audit-field-phone"
              />
            </Field>
            <Field label="Role">
              <PremiumSelect
                value={form.role}
                onChange={setV("role")}
                options={[
                  { value: "Director / Owner", label: "Director / Owner" },
                  { value: "Branch Manager", label: "Branch Manager" },
                  { value: "Sales Progressor", label: "Sales Progressor" },
                  { value: "Sales Negotiator", label: "Sales Negotiator" },
                  { value: "Operations", label: "Operations" },
                  { value: "Other", label: "Other" },
                ]}
                placeholder="Select"
                ariaLabel="Role"
                testid="audit-field-role"
              />
            </Field>
            <Field label="Office size">
              <PremiumSelect
                value={form.officeSize}
                onChange={setV("officeSize")}
                options={[
                  { value: "3",   label: "1–5 people" },
                  { value: "8",   label: "6–10 people" },
                  { value: "20",  label: "11–25 people" },
                  { value: "40",  label: "26–50 people" },
                  { value: "80",  label: "51–100 people" },
                  { value: "200", label: "100+ people" },
                ]}
                ariaLabel="Office size"
                testid="audit-field-office"
              />
            </Field>
          </div>

          {error && (
            <p className="mt-4 text-[12px] font-semibold text-rose-600" data-testid="audit-modal-error">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="mt-5 w-full h-12 rounded-xl text-white text-[13.5px] font-bold inline-flex items-center justify-center gap-2 disabled:opacity-70"
            style={{
              background: "linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)",
              boxShadow: "0 18px 36px -12px rgba(99,102,241,0.6), inset 0 1px 0 rgba(255,255,255,0.22)",
            }}
            data-testid="audit-modal-submit"
          >
            {submitting ? <><Loader2 size={14} className="animate-spin" /> Generating audit…</>
                        : <>Reveal full audit <ArrowRight size={13} /></>}
          </button>

          <p className="mt-3 text-[10.5px] text-slate-500 text-center leading-relaxed">
            We respect your inbox. No spam. You can opt out at any time.
          </p>
        </form>

        <style>{`
          .cf-input {
            width: 100%;
            height: 42px;
            border-radius: 10px;
            padding: 0 12px;
            font-size: 13px;
            color: #0F172A;
            background: #ffffff;
            border: 1px solid rgba(15,23,42,0.10);
            transition: border-color 180ms ease, box-shadow 180ms ease;
            outline: none;
            font-family: inherit;
          }
          .cf-input::placeholder { color: #94A3B8; }
          .cf-input:focus {
            border-color: rgba(124,58,237,0.55);
            box-shadow: 0 0 0 3px rgba(124,58,237,0.12);
          }
          .cf-select { appearance: none; -webkit-appearance: none;
            background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
            background-repeat: no-repeat; background-position: right 12px center; padding-right: 32px;
          }
        `}</style>
      </div>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 block mb-1.5">
      {label}{required && <span className="text-violet-600 ml-0.5">*</span>}
    </span>
    {children}
  </label>
);

const PipelineExposureAudit = () => {
  /* ── Calculator inputs ── */
  const [tx, setTx] = useState(40);
  const [fee, setFee] = useState(1500);
  const [fall, setFall] = useState(30);
  const [delay, setDelay] = useState(28);
  const [hours, setHours] = useState(15);

  /* ── Stage machine: 'calc' | 'analysing' | 'preview' | 'unlocked' ── */
  const [stage, setStage] = useState("calc");
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [serverData, setServerData] = useState(null);

  /* ── Lead capture form state ── */
  const [form, setForm] = useState({
    name: "", agency: "", email: "", phone: "", role: "", officeSize: "8",
  });

  const exposure = useMemo(() => {
    const monthlyAtRisk = tx * (fall / 100) * fee;
    const delayCost     = tx * Math.min(0.10, delay / 360) * fee;
    const hoursCost     = hours * 4 * 12 * 35;
    const annual = Math.round((monthlyAtRisk + delayCost) * 12 + hoursCost);
    return {
      annual,
      monthlyAtRisk: Math.round(monthlyAtRisk),
      atRiskDeals: Math.round(tx * (fall / 100)),
      hoursLost: hours * 4,
      solicitor: Math.round(annual * 0.31),
      pack: Math.round(annual * 0.18),
      search: Math.round(annual * 0.16),
      mortgage: Math.round(annual * 0.08),
    };
  }, [tx, fee, fall, delay, hours]);

  /* Performance tier — inversely correlated with leakage rate. */
  const tier = useMemo(() => {
    const annualRev = Math.max(1, tx * 12 * fee);
    const rate = exposure.annual / annualRev;
    if (rate >= 0.32) return { label: "Operationally Exposed", color: "#E11D48", bg: "#FEF2F2", border: "#FECACA", desc: "Significant operational recovery available", rate };
    if (rate >= 0.20) return { label: "Performing",            color: "#F59E0B", bg: "#FFFBEB", border: "#FDE68A", desc: "Mid-pack with strong upside",            rate };
    if (rate >= 0.12) return { label: "High Momentum",         color: "#3B82F6", bg: "#EFF6FF", border: "#BFDBFE", desc: "Above average operational discipline",   rate };
    return                    { label: "Operationally Elite",  color: "#10B981", bg: "#ECFDF5", border: "#A7F3D0", desc: "Top-quartile operational excellence",     rate };
  }, [exposure.annual, tx, fee]);

  /* Peer benchmark — synthetic but stable. */
  const benchmark = useMemo(() => {
    const yourRatePct = +(tier.rate * 100).toFixed(1);
    const peerRatePct = 14;
    const diff = +(yourRatePct - peerRatePct).toFixed(1);
    return { yourRatePct, peerRatePct, diff };
  }, [tier.rate]);

  /* Single key insight — the largest exposure area, surfaced as a punchy
     consultancy-style takeaway. Stays visible in the preview stage so the
     prospect feels the audit has already delivered value. */
  const keyInsight = useMemo(() => {
    const areas = [
      { id: "solicitor", label: "Solicitor response delays", value: exposure.solicitor, why: "Solicitors typically respond 2.4× slower than top-quartile teams." },
      { id: "pack",      label: "Management pack delays",   value: exposure.pack,      why: "Average pack turnaround is 14 days — ChainFlow's tracked teams hit 6." },
      { id: "search",    label: "Search & enquiry friction", value: exposure.search,    why: "Replies take 2.1× longer when not coordinated through a single thread." },
      { id: "mortgage",  label: "Mortgage expiry risk",      value: exposure.mortgage,  why: "Offers re-expire on 8% of stalled chains — fully avoidable." },
    ];
    const top = areas.reduce((a, b) => (b.value > a.value ? b : a));
    return top;
  }, [exposure]);

  /* Recommended operational moves — surfaced only after unlock. */
  const recommendedMoves = useMemo(() => {
    const moves = [];
    if (delay >= 28)  moves.push({ k: "Lock exchange targets at instruction", v: `Cut ${Math.max(1, Math.round(delay * 0.35))} days from the average exchange cycle.` });
    if (fall >= 25)   moves.push({ k: "Auto-chase stalled solicitor replies",  v: `Recover an estimated ${fmtMoney(Math.round(exposure.solicitor * 0.55))} annually.` });
    if (hours >= 10)  moves.push({ k: "Centralise chase activity in one thread", v: `Reclaim ~${Math.round(hours * 0.6)} hrs of negotiator time each week.` });
    if (exposure.mortgage >= 5000) moves.push({ k: "Monitor mortgage expiry windows", v: `Protect ~${fmtMoney(Math.round(exposure.mortgage * 0.8))} of mortgage-bound revenue.` });
    if (moves.length === 0) moves.push({ k: "Maintain operational discipline", v: "You're already in the top tier — protect the lead with chain-level visibility." });
    return moves.slice(0, 3);
  }, [delay, fall, hours, exposure.solicitor, exposure.mortgage]);

  const onRunAudit = () => {
    setStage("analysing");
  };

  const onAnalysisDone = () => setStage("preview");

  const onUnlock = () => {
    setSubmitError("");
    setShowModal(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.agency.trim() || !form.email.trim()) {
      setSubmitError("Please fill in your name, agency and work email.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${base}/api/audit/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          agency: form.agency.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          role: form.role.trim() || null,
          office_size: Math.max(1, parseInt(form.officeSize, 10) || 1),
          inputs: {
            monthly_transactions: tx,
            avg_fee: fee,
            fall_through_pct: fall,
            avg_delay_days: delay,
            hours_chasing_per_week: hours,
          },
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = Array.isArray(err.detail) ? err.detail[0]?.msg : err.detail;
        throw new Error(msg || "Submission failed. Please try again.");
      }
      const data = await res.json();
      setServerData(data);
      setShowModal(false);
      setStage("unlocked");
      toast.success("Full audit unlocked — we've also emailed your report.");
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Right-column renderer per stage ── */
  const RightColumn = () => {
    if (stage === "calc") {
      return (
        <div data-testid="audit-right-calc">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">Live preview</p>
          <p className="text-[12.5px] text-slate-600 leading-relaxed">
            Adjust your inputs to feel where momentum leaks. When you're ready, run the
            full operational audit — we'll benchmark you against high-performing agencies
            of similar size.
          </p>
          <div className="mt-5 grid grid-cols-3 gap-3">
            {[
              [exposure.atRiskDeals, "Deals at risk / mo"],
              [exposure.hoursLost + " hrs", "Time lost / mo"],
              [Math.round(tier.rate * 100) + "%", "Pipeline leakage"],
            ].map(([v, l]) => (
              <div key={l} className="rounded-xl bg-slate-50/80 border border-slate-100 p-3">
                <p className="font-display text-[18px] font-bold text-slate-900 tabular-nums leading-none">{v}</p>
                <p className="text-[10.5px] text-slate-500 mt-2 leading-snug">{l}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 rounded-xl p-4 flex items-start gap-3"
               style={{ background: "linear-gradient(160deg, #FDF4FF 0%, #EEF2FF 100%)", border: "1px solid #E0E7FF" }}>
            <span className="w-8 h-8 rounded-lg inline-flex items-center justify-center shrink-0"
                  style={{ background: "rgba(124,58,237,0.14)" }}>
              <Sparkles size={14} className="text-violet-700" />
            </span>
            <div>
              <p className="text-[12.5px] font-bold text-slate-900">Run the full operational audit</p>
              <p className="text-[11.5px] text-slate-600 mt-1 leading-snug">
                Reveal your performance tier, top exposure areas and how you compare to
                similar agencies.
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (stage === "preview") {
      return (
        <div data-testid="audit-right-preview" className="cf-fade-up">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">Audit preview</p>
          <div className="rounded-2xl p-5"
               style={{
                 background: "linear-gradient(160deg, #FDF4FF 0%, #EEF2FF 100%)",
                 border: "1px solid #DDD6FE",
                 boxShadow: "0 16px 40px -22px rgba(124,58,237,0.30)",
               }}>
            <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-violet-700">
              Estimated annual revenue leakage
            </p>
            <p
              className="font-display font-bold tracking-[-0.025em] tabular-nums mt-1.5 text-[42px] leading-none"
              style={{ color: "#7C3AED" }}
              data-testid="audit-preview-annual"
            >
              {fmtMoney(exposure.annual)}
            </p>
            <p className="text-[12px] text-slate-600 mt-2.5 leading-relaxed">
              <b className="text-slate-900 tabular-nums">{fmtMoney(exposure.monthlyAtRisk)}</b> at risk each month ·
              <b className="text-slate-900 ml-1 tabular-nums">{exposure.atRiskDeals}</b> deals exposed monthly
            </p>
          </div>

          {/* Exchange delay exposure + Key Insight — visible without unlock */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="audit-preview-insights">
            <div className="rounded-xl p-4"
                 style={{ background: "#FFFFFF", border: "1px solid rgba(15,23,42,0.06)", boxShadow: "0 6px 14px -8px rgba(15,23,42,0.06)" }}>
              <p className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: "#94A3B8" }}>Exchange delay exposure</p>
              <p className="font-display text-[22px] font-bold tabular-nums leading-none mt-1.5" style={{ color: "#0F172A" }} data-testid="audit-preview-delay">
                {delay} days
              </p>
              <p className="text-[10.5px] mt-1.5 leading-snug" style={{ color: "#64748B" }}>
                {delay <= 18 ? "Top-quartile cycle time" : delay <= 28 ? "Mid-pack — recoverable" : "Significant cycle drag"}
              </p>
            </div>
            <div className="rounded-xl p-4"
                 style={{ background: "linear-gradient(160deg, #FFFFFF 0%, #FAFBFD 100%)", border: "1px solid rgba(124,58,237,0.16)", boxShadow: "0 6px 14px -8px rgba(124,58,237,0.12)" }}
                 data-testid="audit-preview-key-insight">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: "#6D28D9" }}>Key insight</p>
              <p className="text-[12.5px] font-bold leading-snug mt-1.5" style={{ color: "#0F172A" }}>
                {keyInsight.label} — <span className="tabular-nums" style={{ color: "#6D28D9" }}>{fmtMoney(keyInsight.value)}</span><span style={{ color: "#64748B", fontWeight: 600 }}> annually</span>
              </p>
              <p className="text-[10.5px] mt-1.5 leading-snug" style={{ color: "#64748B" }}>{keyInsight.why}</p>
            </div>
          </div>

          {/* Blurred deeper intel + unlock CTA */}
          <div className="relative mt-4 rounded-2xl border border-slate-100 overflow-hidden">
            <div aria-hidden="true" className="select-none p-5"
                 style={{ filter: "blur(7px)", opacity: 0.55 }}>
              <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">Performance tier</p>
              <div className="flex items-center gap-3">
                <span className="font-display text-[20px] font-bold text-slate-900">High Momentum</span>
                <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-emerald-600">Above average</span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {["Solicitor delays", "Management pack", "Search/enquiry", "Mortgage expiry"].map((k, i) => (
                  <div key={k} className="rounded-lg bg-slate-50 px-3 py-2 flex items-center justify-between">
                    <span className="text-[11px] text-slate-700">{k}</span>
                    <span className="font-display text-[13px] font-bold text-slate-900 tabular-nums">£{[57.4, 33.3, 29.6, 14.8][i]}K</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 h-12 rounded-lg bg-gradient-to-r from-emerald-100 via-amber-100 to-rose-100" />
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
              <span className="inline-flex items-center gap-1.5 px-2.5 h-[22px] rounded-full"
                    style={{ background: "rgba(124,58,237,0.10)", border: "1px solid rgba(124,58,237,0.18)" }}>
                <Lock size={10} className="text-violet-700" />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-violet-700">3 deeper insights locked</span>
              </span>
              <p className="text-[12.5px] text-slate-700 leading-relaxed max-w-[340px]">
                Performance tier, top exposure breakdown and peer benchmark are ready in your full audit.
              </p>
              <button
                type="button"
                onClick={onUnlock}
                className="h-11 px-5 rounded-xl text-white text-[13px] font-bold inline-flex items-center gap-2"
                style={{
                  background: "linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)",
                  boxShadow: "0 18px 36px -12px rgba(99,102,241,0.6), inset 0 1px 0 rgba(255,255,255,0.22)",
                }}
                data-testid="audit-unlock"
              >
                Unlock full audit <ArrowRight size={13} />
              </button>
              <p className="text-[10.5px] text-slate-500">Takes 30 seconds · delivered instantly</p>
            </div>
          </div>
        </div>
      );
    }

    /* unlocked */
    const benchAhead = benchmark.diff < 0;
    return (
      <div data-testid="audit-right-unlocked" className="cf-fade-up">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Full audit · revealed</p>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-700">
            <Check size={11} strokeWidth={3} /> Emailed to you
          </span>
        </div>

        {/* Headline + tier */}
        <div className="rounded-2xl p-5"
             style={{
               background: "linear-gradient(160deg, #FDF4FF 0%, #EEF2FF 100%)",
               border: "1px solid #DDD6FE",
               boxShadow: "0 16px 40px -22px rgba(124,58,237,0.30)",
             }}>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-violet-700">Annual revenue leakage</p>
          <p className="font-display text-[36px] font-bold tracking-[-0.025em] tabular-nums mt-1 leading-none"
             style={{ color: "#7C3AED" }} data-testid="audit-final-annual">
            {fmtMoney(exposure.annual)}
          </p>
          <div className="mt-3 inline-flex items-center gap-2 px-2.5 h-[26px] rounded-full"
               style={{ background: tier.bg, border: `1px solid ${tier.border}` }}
               data-testid="audit-tier">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: tier.color }} />
            <span className="text-[10.5px] font-bold uppercase tracking-[0.16em]" style={{ color: tier.color }}>
              {tier.label}
            </span>
          </div>
          <p className="text-[11.5px] text-slate-600 mt-2 leading-snug">{tier.desc}.</p>
        </div>

        {/* Top exposure */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Top exposure areas</p>
          {[
            ["Solicitor response delays", exposure.solicitor],
            ["Management pack delays",   exposure.pack],
            ["Search & enquiry delays",  exposure.search],
            ["Mortgage expiry risk",     exposure.mortgage],
          ].map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-[11.5px] py-1.5">
              <span className="text-slate-700">{k}</span>
              <span className="font-bold text-slate-900 tabular-nums">{fmtMoney(v)}</span>
            </div>
          ))}
        </div>

        {/* Benchmark */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-2.5">Compared to similar agencies</p>
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl inline-flex items-center justify-center shrink-0"
                  style={{
                    background: benchAhead ? "rgba(16,185,129,0.10)" : "rgba(225,29,72,0.08)",
                    border: benchAhead ? "1px solid rgba(16,185,129,0.22)" : "1px solid rgba(225,29,72,0.18)",
                  }}>
              {benchAhead ? <TrendingDown size={14} className="text-emerald-600" />
                          : <TrendingUp size={14} className="text-rose-600" />}
            </span>
            <p className="text-[12px] text-slate-700 leading-snug">
              {benchAhead
                ? <>You leak <b className="text-slate-900">{Math.abs(benchmark.diff)}%</b> less pipeline than similar agencies — keep protecting that lead.</>
                : <>Similar agencies typically leak <b className="text-slate-900">{Math.abs(benchmark.diff)}%</b> less pipeline than yours — recoverable with operational discipline.</>
              }
            </p>
          </div>
        </div>

        {/* Recommended operational moves — consultancy-grade deliverable */}
        <div className="mt-4 pt-4 border-t border-slate-100" data-testid="audit-recommended-moves">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-2.5">
            Recommended operational moves
          </p>
          <ul className="space-y-2.5">
            {recommendedMoves.map((m, i) => (
              <li key={m.k} className="flex items-start gap-2.5" data-testid={`audit-move-${i}`}>
                <span
                  className="w-5 h-5 rounded-full inline-flex items-center justify-center text-white text-[10px] font-bold tabular-nums shrink-0 mt-0.5"
                  style={{
                    background: "linear-gradient(135deg, #6D28D9 0%, #4F46E5 100%)",
                    boxShadow: "0 4px 10px -4px rgba(99,102,241,0.45), inset 0 1px 0 rgba(255,255,255,0.22)",
                  }}
                >
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-bold leading-snug" style={{ color: "#0F172A" }}>{m.k}</p>
                  <p className="text-[11px] mt-0.5 leading-snug" style={{ color: "#64748B" }}>{m.v}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <Link
          to="/login"
          className="mt-5 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-violet-700"
          data-testid="audit-after-cta"
        >
          See how ChainFlow recovers this revenue <ArrowRight size={13} />
        </Link>
      </div>
    );
  };

  return (
    <section className="max-w-[1320px] mx-auto px-6 lg:px-10 pb-20" data-testid="landing-audit">
      <SoftCard className="p-6 lg:p-8 relative">
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,0.7fr)_minmax(0,1.1fr)_minmax(0,1fr)] gap-8 items-start">
          <div>
            <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-violet-700">Pipeline exposure audit</p>
            <h2 className="font-display text-[28px] lg:text-[32px] font-bold tracking-[-0.022em] text-slate-900 leading-[1.08] mt-3">
              How much is poor progression costing your business?
            </h2>
            <p className="text-[13px] text-slate-600 leading-relaxed mt-3">
              See your estimated revenue at risk from delays, fall-throughs and operational friction —
              with a peer benchmark and performance tier.
            </p>
            <ul className="mt-4 space-y-2">
              {[
                "Built for UK estate agencies",
                "Benchmarked against high-performing teams",
                "No card required · 30 seconds",
              ].map((t) => (
                <li key={t} className="inline-flex items-center gap-2 text-[12.5px] text-slate-700">
                  <span className="w-4 h-4 rounded-full bg-violet-100 text-violet-700 inline-flex items-center justify-center">
                    <Check size={10} strokeWidth={3} />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className={stage === "analysing" ? "transition-opacity duration-500 opacity-50" : ""}>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">Your inputs</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10.5px] text-slate-500 mb-1.5">Monthly transactions</p>
                <PremiumStepper value={tx} onChange={setTx} lo={0} hi={500} step={5} testid="audit-tx" />
              </div>
              <div>
                <p className="text-[10.5px] text-slate-500 mb-1.5">Average fee per deal (£)</p>
                <PremiumStepper value={fee} onChange={setFee} lo={200} hi={20000} step={100} prefix="£" testid="audit-fee" />
              </div>
              <div>
                <p className="text-[10.5px] text-slate-500 mb-1.5">Fall-through rate (%)</p>
                <PremiumStepper value={fall} onChange={setFall} lo={0} hi={100} step={5} suffix="%" testid="audit-fall" />
              </div>
              <div>
                <p className="text-[10.5px] text-slate-500 mb-1.5">Average delay to exchange</p>
                <PremiumStepper value={delay} onChange={setDelay} lo={0} hi={120} step={1} suffix=" days" testid="audit-delay" />
              </div>
              <div className="col-span-2">
                <p className="text-[10.5px] text-slate-500 mb-1.5">Hours spent chasing per week</p>
                <PremiumStepper value={hours} onChange={setHours} lo={0} hi={80} step={1} testid="audit-hours" />
              </div>
            </div>
            {stage === "calc" || stage === "analysing" ? (
              <button
                type="button"
                onClick={onRunAudit}
                disabled={stage === "analysing"}
                className="mt-4 w-full h-11 rounded-xl text-white text-[13px] font-bold inline-flex items-center justify-center gap-2 disabled:opacity-70"
                style={{
                  background: "linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)",
                  boxShadow: "0 14px 28px -10px rgba(99,102,241,0.55), inset 0 1px 0 rgba(255,255,255,0.18)",
                }}
                data-testid="audit-run"
              >
                {stage === "analysing" ? <><Loader2 size={13} className="animate-spin" /> Analysing…</>
                                       : <>Run audit <ArrowRight size={13} /></>}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => { setStage("calc"); setServerData(null); }}
                className="mt-4 w-full h-11 rounded-xl text-[13px] font-semibold inline-flex items-center justify-center gap-2 text-slate-700 bg-white border border-slate-200 hover:border-slate-300"
                data-testid="audit-reset"
              >
                Recalculate
              </button>
            )}
          </div>

          <div className="relative">
            <RightColumn />
          </div>
        </div>

        {stage === "analysing" && <AnalysisOverlay onDone={onAnalysisDone} />}
      </SoftCard>

      <LeadCaptureModal
        open={showModal}
        onClose={() => !submitting && setShowModal(false)}
        onSubmit={onSubmit}
        form={form}
        setForm={setForm}
        submitting={submitting}
        error={submitError}
        headlineLeakage={fmtMoney(exposure.annual)}
      />

      {/* Helper class used by overlay & previews */}
      <style>{`
        .cf-fade-up { animation: cf-fade-up 520ms cubic-bezier(0.22, 1, 0.36, 1) both; }
        @keyframes cf-fade-up {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

/* ─── Legacy Footer — deleted in Phase 5 hotfix.
   The white MarketingFooter (see components/landing/MarketingFooter.jsx)
   is the only footer on the public homepage. This dead-code definition
   was removed to (a) guarantee it can never accidentally re-render
   and (b) force the production JS bundle hash to change so CDN caches
   invalidate. Do not restore this component. ─── */

/* ─── Page ───────────────────────────────────────── */
export default function LandingPage() {
  return (
    <div className="min-h-screen overflow-x-clip" style={{ background: "#FAFAFE" }} data-testid="landing-page">
      <TopNav />
      <Hero />
      <KPIs />
      <ProblemTransition />
      <MarketingProperties />
      <MarketingDealRoom />
      <MarketingIntelligence />
      <MarketingCommunication />
      <MarketingAgencyFit />
      <MarketingOutcomes />
      <MarketingReferrals />
      <MarketingHealthCheck />
      <MarketingFooter />
    </div>
  );
}
